// src/layouts/annotator/exams/annotation.js
// Dynamic: fetch exam by exam_id, build tabs from sample types, list recordings
// Shows per-recording status & enables "Annotate" if *your* status !== "Submitted"

import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CommentIcon from "@mui/icons-material/Comment";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.esm.js";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
  getExamBundle,
  saveAnnotation,
  getAnnotation,
  markExamComplete,
  saveAnnotationsBulk,
  getAdjacentExamsWithFilter,
  updateRecordingDuration,
} from "services/sampleService";
import { useTranslation } from "hooks/useTranslation";
import { translateSampleType as translateSampleTypeLabel } from "utils/sampleTypeI18n";
import { API_BASE } from "config/api";
import SplitAnnotationPlayer from "components/SplitAnnotationPlayer";
import SegmentAnnotationFields from "components/SegmentAnnotationFields";
import {
  DISABLED_BUTTON_SX,
  DISABLED_FIELD_SX,
  DISABLED_OUTLINED_INPUT_SX,
} from "utils/disabledFieldStyles";
import {
  SEG_LIMIT,
  generateDefaultSplitTimes,
  syncSegmentData,
  applySplitTimesChange,
  getSegments,
  isLongPassageRecording,
  isPassageSample,
  shouldRefreshDuration,
  buildSplitAnnotationPayload,
} from "utils/segmentAnnotation";

const proxiedAudioUrl = (originalUrl) => {
  if (!originalUrl) return originalUrl;
  const url = new URL("api/audio/proxy", API_BASE);
  url.searchParams.set("url", originalUrl);
  return url.toString();
};

// --- Constants and Utility Functions ---

const useQuery = () => new URLSearchParams(useLocation().search);

function getLoggedInAnnotatorId() {
  const candidates = [localStorage.getItem("user_id")].filter(Boolean);
  if (!candidates.length) return null;
  const v = candidates[0];
  const n = Number(v);
  return Number.isNaN(n) ? v : n;
}

const BG_NOISE_SPEC = [
  { value: "Not Applicable", i18nKey: "notApplicable" },
  { value: "Motor Bike", i18nKey: "motorBike" },
  { value: "Machine Noise", i18nKey: "machineNoise" },
  { value: "Animals", i18nKey: "animals" },
  { value: "Other", i18nKey: "other" },
];

const NOT_TRANSCRIBABLE_SPEC = [
  { value: "Not Applicable", i18nKey: "notApplicable" },
  { value: "Blank Audio", i18nKey: "blankAudio" },
  { value: "Mispronounced", i18nKey: "mispronounced" },
  { value: "Too Noisy", i18nKey: "tooNoisy" },
  { value: "Language Mismatch", i18nKey: "languageMismatch" },
  { value: "Other", i18nKey: "other" },
];

function getReadCorrectOptions(t) {
  return [
    { value: "Yes", label: t("annotator.annotationPage.options.yes", "Yes") },
    { value: "No", label: t("annotator.annotationPage.options.no", "No") },
  ];
}

function getFluidityOptions(t) {
  return [
    { value: "Yes", label: t("annotator.annotationPage.options.yes", "Yes") },
    { value: "No", label: t("annotator.annotationPage.options.no", "No") },
  ];
}

function isFluiditySampleType(sampleType) {
  return sampleType === "Sentence" || sampleType === "Passage";
}

function resolveFluiditySelectedValue(fluidity) {
  if (fluidity === true || fluidity === 1) return "Yes";
  if (fluidity === false || fluidity === 0) return "No";
  return undefined;
}

function resolveFluidityForPayload(fluidity) {
  if (fluidity === true || fluidity === 1) return 1;
  if (fluidity === false || fluidity === 0) return 0;
  return null;
}

function getBgNoiseOptions(t) {
  return BG_NOISE_SPEC.map(({ value, i18nKey }) => ({
    value,
    label: t(`annotator.annotationPage.bgNoise.${i18nKey}`, value),
  }));
}

function getNotTranscribableOptions(t) {
  return NOT_TRANSCRIBABLE_SPEC.map(({ value, i18nKey }) => ({
    value,
    label: t(`annotator.annotationPage.notTranscribable.${i18nKey}`, value),
  }));
}

function isNonTranscribable(value) {
  const normalized = value != null ? String(value).trim() : "";
  if (!normalized || normalized === "Not Applicable") return false;
  if (normalized === "Select Reason" || normalized === "Select reason") return false;
  return true;
}

function resolveReadCorrectlySelectedValue(readCorrectly) {
  if (readCorrectly === true || readCorrectly === 1) return "Yes";
  if (readCorrectly === false || readCorrectly === 0) return "No";
  return undefined;
}

function getPlaceholder(kind, t) {
  if (kind === "selectReason") {
    return {
      value: "Select Reason",
      label: t("annotator.annotationPage.placeholders.selectReason", "Select reason"),
    };
  }
  if (kind === "selectCorrect") {
    return {
      value: "Select Correct",
      label: t("annotator.annotationPage.placeholders.selectCorrect", "Select"),
    };
  }
  return {
    value: "Select",
    label: t("annotator.annotationPage.placeholders.select", "Select"),
  };
}

// Helper function to fix UTF-8 encoding issues (French / Wolof / Latin-1 mojibake)
const fixUTF8Encoding = (text) => {
  if (!text) return text;

  try {
    if (!/Ã|Å/.test(text)) {
      return text;
    }
    return text
      .replace(/Ã±/g, "ñ")
      .replace(/Ã³/g, "ó")
      .replace(/Ã©/g, "é")
      .replace(/Ã¨/g, "è")
      .replace(/Ã­/g, "í")
      .replace(/Ã¡/g, "á")
      .replace(/Ãº/g, "ú")
      .replace(/Ã /g, "à")
      .replace(/\u00C3\u00A0/g, "à")
      .replace(/Ã®/g, "î")
      .replace(/Ã´/g, "ô")
      .replace(/Ã¹/g, "ù")
      .replace(/Ã§/g, "ç")
      .replace(/Ã¶/g, "ö")
      .replace(/Ã–/g, "Ö")
      .replace(/Ãœ/g, "Ü")
      .replace(/Ã¼/g, "ü")
      .replace(/Ã\u00AD/g, "í")
      .replace(/Ã\u00B3/g, "ó")
      .replace(/Å"/g, "œ")
      .replace(/Å'/g, "œ");
  } catch (e) {
    console.error("Error fixing encoding:", e);
    return text;
  }
};

// Helper function to split text into lines
// Passage rules:
// 1. Line 1 = title
// 2. Line 2 = empty → remove it
// 3. Line 3 = passage → split by full stop, ignore last punctuation
const splitTextIntoLines = (text, isPassage = false) => {
  if (text == null) {
    return [""];
  }

  // Fix encoding issues before processing
  const fixedText = fixUTF8Encoding(text);
  const strText = String(fixedText).trim();
  if (strText === "") {
    return [""];
  }

  // Always split by newline first
  let lines = strText.split("\n").map((line) => line.trim());

  if (!isPassage) {
    return lines;
  }

  // ---- PASSAGE HANDLING ----

  // A. Line 1 is title
  const title = lines[0] || "";

  // B. Remove line 2 if it is empty
  let passageLineIndex = 1;
  if (lines[1] === "") {
    passageLineIndex = 2;
  }

  const passageText = lines.slice(passageLineIndex).join(" ").trim();

  if (!passageText) {
    return [title];
  }

  // C. Split passage by full stop and ignore trailing punctuation
  const sentences = passageText
    .match(/[^.]+\.?/g)
    .map((s) => s.trim())
    .filter(Boolean);

  return [title, ...sentences];
};

function countWordsInText(text) {
  const normalized = String(text ?? "").trim();
  if (!normalized) return 1;
  return Math.max(1, normalized.split(/\s+/).filter(Boolean).length);
}

function getMaxMistakesForRecording(recording) {
  const sampleType = recording?.sample_type;
  if (sampleType === "Letter" || sampleType === "Word" || sampleType === "Non-word") {
    return 1;
  }
  if (sampleType === "Sentence" || isPassageSample(recording)) {
    return countWordsInText(recording?.sample_text);
  }
  return countWordsInText(recording?.sample_text);
}

function recordingSupportsMistakesValidation(recording) {
  if (String(recording?.is_no_mistakes ?? "0") === "1") return true;
  return isPassageSample(recording);
}

function validateMistakesBeforeSave(recording, hasNonTranscribable) {
  if (!recordingSupportsMistakesValidation(recording) || hasNonTranscribable) {
    return null;
  }
  const maxMistakes = getMaxMistakesForRecording(recording);
  const mistakes = Number(recording?.annotation?.no_of_mistakes ?? 0);
  if (mistakes > maxMistakes) {
    return {
      ok: false,
      reason: "mistakes_exceed_max",
      messageKey: "annotator.annotationPage.validation.mistakesExceedMax",
      maxMistakes,
    };
  }
  return null;
}

function formatBulkSaveFailureMessage(result, fallback) {
  const failed = (result?.results || []).find((entry) => entry.status === "error");
  return failed?.message || result?.message || fallback;
}

function buildAnnotationClearedForNonTranscribable(rec, notTranscribableValue) {
  const isPassage = isPassageSample(rec);
  const canonical = splitTextIntoLines(rec?.sample_text || "", isPassage);
  const lineCount = Math.max(1, canonical.length);
  const emptyLines = Array.from({ length: lineCount }, () => "");
  const emptyTimestamps = Array.from({ length: lineCount }, () => ({ start: null, end: null }));

  const clearedSegments = (rec.annotation?.segments || []).map((seg) => ({
    ...seg,
    reference_text: "",
    transcription_text: "",
  }));

  return {
    ...rec.annotation,
    not_transcribable: notTranscribableValue,
    transcription_lines: emptyLines,
    transcription_text: "",
    audio_timestamps: emptyTimestamps,
    read_correctly: null,
    no_of_mistakes: 0,
    bg_noise: "",
    fluidity: null,
    disfluency_occurences: 0,
    segments: clearedSegments,
  };
}

// Helper function to join lines back into a single text WITH POSITION PRESERVATION
const joinLinesIntoText = (lines) => {
  if (!lines || !Array.isArray(lines) || lines.length === 0) {
    return "";
  }

  //if line is empty string , add hypen only in case of passage or para

  if (lines.length > 1) {
    lines = lines.map((line) => (line == "" ? "-" : line));
  }

  // Join with newline character to preserve line positions
  // Empty lines become actual newlines
  return lines.join("\n");
};

function parseAudioTimestampsFromDb(raw, canonicalLineCount) {
  let audioTimestamps = [];
  if (raw) {
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      audioTimestamps = Array.isArray(parsed)
        ? parsed.map((it) => {
            if (!it || typeof it !== "object") {
              return { start: null, end: null };
            }
            return {
              start:
                it.start_timestamp ??
                it.mark_start_timestamp ??
                (it.mark_end_timestamp && typeof it.mark_end_timestamp === "object"
                  ? it.mark_end_timestamp.start
                  : null) ??
                null,
              end:
                it.end_timestamp ??
                (it.mark_end_timestamp && typeof it.mark_end_timestamp === "object"
                  ? it.mark_end_timestamp.end
                  : typeof it.mark_end_timestamp === "string"
                  ? it.mark_end_timestamp
                  : null) ??
                null,
            };
          })
        : [];
    } catch (e) {
      audioTimestamps = [];
    }
  }
  if (audioTimestamps.length < canonicalLineCount) {
    audioTimestamps = [
      ...audioTimestamps,
      ...Array.from({ length: canonicalLineCount - audioTimestamps.length }, () => ({
        start: null,
        end: null,
      })),
    ];
  } else if (audioTimestamps.length > canonicalLineCount) {
    audioTimestamps = audioTimestamps.slice(0, canonicalLineCount);
  }
  return audioTimestamps;
}

function buildAudioTimestampsPayload(recording, isPassageRow) {
  const canonical = splitTextIntoLines(recording.sample_text, isPassageRow);
  const transcriptionLines =
    recording.annotation.transcription_lines ||
    splitTextIntoLines(recording.annotation.transcription_text || "");
  const lines = Array.isArray(transcriptionLines) ? transcriptionLines : [""];
  const tsArr = recording.annotation.audio_timestamps || [];
  return canonical.map((sentence, i) => ({
    text: sentence,
    transcript: lines[i] || "",
    start_timestamp: tsArr[i]?.start ?? null,
    end_timestamp: tsArr[i]?.end ?? null,
  }));
}

function buildAnnotationSavePayload(recording, annotatorId) {
  const isPassageRow = isPassageSample(recording);
  const isLongPassage = isLongPassageRecording(recording);

  const basePayload = {
    recording_id: recording.recording_id,
    annotator_id: annotatorId,
    read_correctly: recording.annotation.read_correctly || false,
    bg_noise: recording.annotation.bg_noise || "",
    not_transcribable: recording.annotation.not_transcribable || "",
    round: 1,
    no_of_mistakes:
      recording.annotation.no_of_mistakes === "" ||
      recording.annotation.no_of_mistakes === undefined
        ? 0
        : recording.annotation.no_of_mistakes,
    disfluency_occurences:
      recording.annotation.disfluency_occurences === "" ||
      recording.annotation.disfluency_occurences === undefined
        ? 0
        : recording.annotation.disfluency_occurences,
    fluidity: resolveFluidityForPayload(recording.annotation.fluidity),
  };

  if (isLongPassage) {
    const splitPayload = buildSplitAnnotationPayload(recording);
    return {
      ...basePayload,
      transcription_text: splitPayload.transcription_text,
      audio_timestamps: null,
      split_times: splitPayload.split_times,
      segments: splitPayload.segments,
    };
  }

  const transcriptionLines =
    recording.annotation.transcription_lines ||
    splitTextIntoLines(recording.annotation.transcription_text || "");
  const lines = Array.isArray(transcriptionLines) ? transcriptionLines : [""];
  const concatenatedText = joinLinesIntoText(lines);

  return {
    ...basePayload,
    transcription_text: concatenatedText || "",
    audio_timestamps: buildAudioTimestampsPayload(recording, isPassageRow),
  };
}

function validateRecordingBeforeSave(recording, options = {}) {
  if (isLongPassageRecording(recording)) {
    return validateSplitPassageBeforeSave(recording, options);
  }

  const notTranscribableRaw = recording?.annotation?.not_transcribable;
  const hasNonTranscribable = isNonTranscribable(notTranscribableRaw);

  const isPassage = isPassageSample(recording);
  const canonical = splitTextIntoLines(recording?.sample_text || "", isPassage);
  const transcriptionLines = recording?.annotation?.transcription_lines || [];
  const audioTimestamps = recording?.annotation?.audio_timestamps || [];
  const requiresTimestamps = isFluiditySampleType(recording?.sample_type);

  let everyLineFullyFilled = true;
  let everyLineFullyEmpty = true;

  canonical.forEach((_, i) => {
    const transcript = String(transcriptionLines[i] || "").trim();
    const start = audioTimestamps[i]?.start;
    const end = audioTimestamps[i]?.end;

    const tFilled = transcript !== "" && transcript !== "-";
    const sFilled = start != null && start !== "";
    const eFilled = end != null && end !== "";

    const lineFilled = requiresTimestamps ? tFilled && sFilled && eFilled : tFilled;
    const lineHasData = requiresTimestamps ? tFilled || sFilled || eFilled : tFilled;

    if (!lineFilled) everyLineFullyFilled = false;
    if (lineHasData) everyLineFullyEmpty = false;
  });

  if (isPassage) {
    if (hasNonTranscribable && !everyLineFullyEmpty) {
      return {
        ok: false,
        reason: "passage_partial_with_nt",
        messageKey: "annotator.annotationPage.validation.passagePartialWithNt",
      };
    }
    if (everyLineFullyEmpty && !hasNonTranscribable) {
      return {
        ok: false,
        reason: "passage_all_empty",
        messageKey: "annotator.annotationPage.validation.passageAllEmpty",
      };
    }
    const mistakesValidation = validateMistakesBeforeSave(recording, hasNonTranscribable);
    if (mistakesValidation) return mistakesValidation;
    return { ok: true };
  }

  const mistakesMessageKey = "annotator.annotationPage.validation.mistakesExceedMax";
  const mistakesValidation = validateMistakesBeforeSave(recording, hasNonTranscribable);
  if (mistakesValidation) {
    return { ...mistakesValidation, messageKey: mistakesMessageKey };
  }

  if (hasNonTranscribable) {
    if (everyLineFullyEmpty) return { ok: true };
    return {
      ok: false,
      reason: "B_violation",
      messageKey: requiresTimestamps
        ? "annotator.annotationPage.validation.bViolation"
        : "annotator.annotationPage.validation.bViolationNoTimestamps",
    };
  }

  if (everyLineFullyFilled) return { ok: true };
  return {
    ok: false,
    reason: "A_violation",
    messageKey: requiresTimestamps
      ? "annotator.annotationPage.validation.aViolation"
      : "annotator.annotationPage.validation.aViolationNoTimestamps",
  };
}

function validateSplitPassageBeforeSave(recording, { strict = false } = {}) {
  const notTranscribableRaw = recording?.annotation?.not_transcribable;
  const hasNonTranscribable = isNonTranscribable(notTranscribableRaw);

  const duration = Number(recording?.duration) || 0;
  const splitTimes = recording?.annotation?.split_times || [];
  const segments = recording?.annotation?.segments || [];
  const segs = getSegments(splitTimes, duration);

  if (segs.some((s) => s.end - s.start > SEG_LIMIT)) {
    return {
      ok: false,
      reason: "segment_over_limit",
      messageKey: "annotator.annotationPage.validation.segmentOverLimit",
    };
  }

  if (hasNonTranscribable) {
    const anySegmentFilled = segments.some(
      (s) => String(s?.reference_text || "").trim() || String(s?.transcription_text || "").trim()
    );
    if (anySegmentFilled) {
      return {
        ok: false,
        reason: "split_partial_with_nt",
        messageKey: "annotator.annotationPage.validation.splitPartialWithNt",
      };
    }
    return { ok: true };
  }

  if (strict) {
    const allSegmentsComplete =
      segments.length === segs.length &&
      segments.every(
        (s) => String(s?.reference_text || "").trim() && String(s?.transcription_text || "").trim()
      );

    if (!allSegmentsComplete) {
      return {
        ok: false,
        reason: "segment_incomplete",
        messageKey: "annotator.annotationPage.validation.segmentIncomplete",
      };
    }
  }

  const mistakesValidation = validateMistakesBeforeSave(recording, hasNonTranscribable);
  if (mistakesValidation) return mistakesValidation;

  return { ok: true };
}

const VALIDATION_MESSAGE_FALLBACKS = {
  "annotator.annotationPage.validation.aViolation":
    "Fill transcript and Mark Start/End for every line, or pick a Non-Transcribable reason (and leave them all empty).",
  "annotator.annotationPage.validation.aViolationNoTimestamps":
    "Fill transcript for every line, or pick a Non-Transcribable reason (and leave them all empty).",
  "annotator.annotationPage.validation.bViolation":
    "Non-Transcribable is selected — clear transcripts and Mark Start/End timestamps, or unselect Non-Transcribable and fill them in completely.",
  "annotator.annotationPage.validation.bViolationNoTimestamps":
    "Non-Transcribable is selected — clear all transcripts, or unselect Non-Transcribable and fill them in completely.",
  "annotator.annotationPage.validation.mistakesExceedMax":
    "Number of mistakes cannot exceed {{max}} for this sample.",
  "annotator.annotationPage.validation.passagePartialWithNt":
    "Non-Transcribable is only allowed when all sentences are blank. Clear all transcripts and Mark Start/End, or unselect Non-Transcribable.",
  "annotator.annotationPage.validation.passageAllEmpty":
    "Fill transcript / Mark Start/End for at least one sentence, or select a Non-Transcribable reason.",
  "annotator.annotationPage.validation.segmentOverLimit":
    "One or more segments exceed 2:00. Drag split lines to natural pauses so each segment stays under 2:00.",
  "annotator.annotationPage.validation.segmentIncomplete":
    "Fill reference and annotated text for every segment, or select a Non-Transcribable reason.",
  "annotator.annotationPage.validation.splitPartialWithNt":
    "Non-Transcribable is only allowed when all segments are blank. Clear segment text or unselect Non-Transcribable.",
};

function formatValidationMessage(validation, t) {
  if (!validation?.messageKey) return "";
  const fallback = VALIDATION_MESSAGE_FALLBACKS[validation.messageKey] || "";
  let message = t(validation.messageKey, fallback);
  if (validation.maxMistakes != null) {
    message = message.replace("{{max}}", String(validation.maxMistakes));
  }
  return message;
}

const AnnotationTextField = ({ disabled = false, sx, ...props }) => (
  <TextField
    {...props}
    disabled={disabled}
    sx={{
      ...(disabled ? DISABLED_OUTLINED_INPUT_SX : {}),
      ...sx,
    }}
  />
);

AnnotationTextField.propTypes = {
  disabled: PropTypes.bool,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};

// --- Copy Button Component ---
const CopyButton = ({ onClick, disabled = false, title }) => {
  const { t } = useTranslation();
  const tip = title ?? t("annotator.annotationPage.copy.toTranscription", "Copy to transcription");
  return (
    <Tooltip title={tip} placement="top">
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{
          position: "absolute",
          right: 4,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1,
          backgroundColor: "white",
          "&:hover": {
            backgroundColor: "#f5f5f5",
          },
          "&.Mui-disabled": {
            backgroundColor: "#eef2f7",
            opacity: 0.55,
          },
          width: 28,
          height: 28,
        }}
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

CopyButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  title: PropTypes.string,
};

// --- Select Field Component ---

const SelectField = ({
  field,
  options,
  placeholder,
  selectedValue,
  onChange,
  disabled = false,
}) => {
  const [openDropdown, setOpenDropdown] = useState(false);
  const resolved =
    selectedValue === undefined || selectedValue === null || selectedValue === ""
      ? placeholder.value
      : selectedValue;
  const selectedLabel =
    resolved === placeholder.value
      ? placeholder.label
      : options.find((opt) => opt.value === resolved)?.label ?? resolved;

  return (
    <Box sx={{ minWidth: 0, width: "100%" }}>
      <FormControl
        fullWidth
        sx={{
          height: 50,
          minWidth: 0,
          ...(disabled
            ? {
                backgroundColor: "#eef2f7",
                borderRadius: 1,
              }
            : {}),
        }}
      >
        <Select
          className="pointer"
          labelId={`${field}-label`}
          value={resolved}
          onChange={(e) => {
            const val = e.target.value === "__CLEAR__" ? "" : e.target.value;
            onChange({ target: { value: val } });
          }}
          onOpen={() => setOpenDropdown(true)}
          onClose={() => setOpenDropdown(false)}
          IconComponent={() => (
            <FontAwesomeIcon icon={openDropdown ? faChevronUp : faChevronDown} />
          )}
          disabled={disabled}
          renderValue={() => (
            <Box
              component="span"
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
            >
              {selectedLabel}
            </Box>
          )}
          sx={{
            height: 50,
            minWidth: 0,
            fontSize: "0.8rem",
            ...(disabled ? DISABLED_FIELD_SX : {}),
            "& .MuiSelect-select": {
              display: "block !important",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              paddingRight: "32px !important",
            },
            "& svg": {
              top: "calc(50% - 0.5em)",
              right: 10,
              position: "absolute",
              pointerEvents: "none",
            },
          }}
        >
          <MenuItem value={placeholder.value} disabled>
            {placeholder.label}
          </MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

SelectField.propTypes = {
  field: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired })
  ).isRequired,
  placeholder: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  selectedValue: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// Fixed pixel column widths — CSS Grid keeps all columns on one row; parent scrolls horizontally
const TABLE_COL_PX = {
  recording: 200,
  canonical: 170,
  transcribed: 170,
  timestamps: 190,
  readCorrectly: 130,
  mistakes: 110,
  bgNoise: 140,
  nonTranscribable: 160,
  fluidity: 130,
  disfluency: 140,
  actions: 90,
};

const TABLE_COL_GAP = 16;

function buildTableRowSx(showMistakes, showFluidity, showDisfluency, showTimestamps = false) {
  const widths = [
    TABLE_COL_PX.recording,
    TABLE_COL_PX.canonical,
    TABLE_COL_PX.nonTranscribable,
    TABLE_COL_PX.transcribed,
  ];
  if (showTimestamps) widths.push(TABLE_COL_PX.timestamps);
  widths.push(TABLE_COL_PX.readCorrectly);
  if (showMistakes) widths.push(TABLE_COL_PX.mistakes);
  widths.push(TABLE_COL_PX.bgNoise);
  if (showFluidity) widths.push(TABLE_COL_PX.fluidity);
  if (showDisfluency) widths.push(TABLE_COL_PX.disfluency);
  widths.push(TABLE_COL_PX.actions);

  const minWidth = widths.reduce((sum, w) => sum + w, 0) + (widths.length - 1) * TABLE_COL_GAP;

  return {
    display: "grid",
    gridTemplateColumns: widths.map((w) => `${w}px`).join(" "),
    columnGap: `${TABLE_COL_GAP}px`,
    alignItems: "center",
    minWidth,
    width: "max-content",
  };
}

// --- Header Row Component (conditional columns) ---
const HeaderRow = ({
  showDisfluency = false,
  showMistakes = false,
  showFluidity = false,
  showTimestamps = false,
}) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        ...buildTableRowSx(showMistakes, showFluidity, showDisfluency, showTimestamps),
        py: 1,
        borderBottom: "1px solid #e6e6e6",
      }}
    >
      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          {t("annotator.annotationPage.columns.recording", "Recording")}
        </Typography>
        <Tooltip
          title={t("annotator.annotationPage.tooltips.playAudio", "Play the audio for this sample")}
          placement="top"
        >
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>

      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          {t("annotator.annotationPage.columns.canonicalText", "Canonical text")}
        </Typography>
        <Tooltip
          title={t(
            "annotator.annotationPage.tooltips.canonicalHint",
            "Expected/reference text for the recording"
          )}
          placement="top"
        >
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>

      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          {t("annotator.annotationPage.columns.nonTranscribable", "Non-transcribable")}
        </Typography>
        <Tooltip
          title={t(
            "annotator.annotationPage.tooltips.nonTranscribableHint",
            "Reason if the audio cannot be transcribed"
          )}
          placement="top"
        >
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>

      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          {t("annotator.annotationPage.columns.transcribedText", "Transcribed text")}
        </Typography>
        <Tooltip
          title={t(
            "annotator.annotationPage.tooltips.transcribedHint",
            "What you transcribe from the audio (enter line by line)"
          )}
          placement="top"
        >
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>

      {showTimestamps && (
        <Typography fontWeight="bold" align="center" fontSize="0.875rem">
          {t("annotator.annotationPage.columns.timestamps", "Timestamps")}
        </Typography>
      )}

      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          {t("annotator.annotationPage.columns.readCorrectly", "Read correctly?")}
        </Typography>
        <Tooltip
          title={t(
            "annotator.annotationPage.tooltips.readCorrectlyHint",
            "Was pronunciation correct?"
          )}
          placement="top"
        >
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>

      {showMistakes && (
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            {t("annotator.annotationPage.columns.noOfMistakes", "No. of mistakes")}
          </Typography>
          <Tooltip
            title={t(
              "annotator.annotationPage.tooltips.mistakesHint",
              "Number of pronunciation/word errors in the sample"
            )}
            placement="top"
          >
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </Box>
      )}

      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          {t("annotator.annotationPage.columns.bgNoise", "BG noise")}
        </Typography>
        <Tooltip
          title={t("annotator.annotationPage.tooltips.bgNoiseHint", "Background noise type if any")}
          placement="top"
        >
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>

      {showFluidity && (
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            {t("annotator.annotationPage.columns.fluidity", "Fluidity")}
          </Typography>
          <Tooltip
            title={t(
              "annotator.annotationPage.tooltips.fluidityHint",
              "Is the reading fluent? (reads as continuous sentences, not word by word)"
            )}
            placement="top"
          >
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </Box>
      )}

      {showDisfluency && (
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            {t("annotator.annotationPage.columns.disfluencyOccurrences", "Disfluency occurrences")}
          </Typography>
          <Tooltip
            title={t(
              "annotator.annotationPage.tooltips.disfluencyHint",
              "Number of disfluency occurrences in the recording"
            )}
            placement="top"
          >
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </Box>
      )}

      <Typography fontWeight="bold" align="center" fontSize="0.875rem">
        {t("annotator.annotationPage.columns.actions", "Actions")}
      </Typography>
    </Box>
  );
};

HeaderRow.propTypes = {
  showDisfluency: PropTypes.bool,
  showMistakes: PropTypes.bool,
  showFluidity: PropTypes.bool,
  showTimestamps: PropTypes.bool,
};

const LineTimestampControls = ({
  lineIndex,
  audioTimestamps,
  disabled,
  onMarkStart,
  onMarkEnd,
  onClearStart,
  onClearEnd,
}) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap" }}>
        {audioTimestamps[lineIndex]?.start != null ? (
          <>
            <Button
              size="small"
              color="success"
              variant="outlined"
              disabled
              startIcon={<SaveIcon fontSize="small" />}
              sx={{ textTransform: "none", whiteSpace: "nowrap", minWidth: 0, px: 1 }}
            >
              {audioTimestamps[lineIndex].start}
            </Button>
            <IconButton
              size="small"
              onClick={() => onClearStart(lineIndex)}
              disabled={disabled}
              aria-label="clear start timestamp"
              sx={disabled ? DISABLED_BUTTON_SX : undefined}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => onMarkStart(lineIndex)}
            disabled={disabled}
            sx={{
              textTransform: "none",
              whiteSpace: "nowrap",
              minWidth: 0,
              px: 1.5,
              color: "#fff",
              ...DISABLED_BUTTON_SX,
            }}
          >
            {t("annotator.annotationPage.buttons.markStart", "Mark start")}
          </Button>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap" }}>
        {audioTimestamps[lineIndex]?.end != null ? (
          <>
            <Button
              size="small"
              color="success"
              variant="outlined"
              disabled
              startIcon={<SaveIcon fontSize="small" />}
              sx={{ textTransform: "none", whiteSpace: "nowrap", minWidth: 0, px: 1 }}
            >
              {audioTimestamps[lineIndex].end}
            </Button>
            <IconButton
              size="small"
              onClick={() => onClearEnd(lineIndex)}
              disabled={disabled}
              aria-label="clear end timestamp"
              sx={disabled ? DISABLED_BUTTON_SX : undefined}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => onMarkEnd(lineIndex)}
            disabled={disabled}
            sx={{
              textTransform: "none",
              whiteSpace: "nowrap",
              minWidth: 0,
              px: 1.5,
              color: "#fff",
              ...DISABLED_BUTTON_SX,
            }}
          >
            {t("annotator.annotationPage.buttons.markEnd", "Mark end")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

LineTimestampControls.propTypes = {
  lineIndex: PropTypes.number.isRequired,
  audioTimestamps: PropTypes.array.isRequired,
  disabled: PropTypes.bool.isRequired,
  onMarkStart: PropTypes.func.isRequired,
  onMarkEnd: PropTypes.func.isRequired,
  onClearStart: PropTypes.func.isRequired,
  onClearEnd: PropTypes.func.isRequired,
};

const useRecordingWaveform = (
  recordingFile,
  audioTimestamps,
  { recordingId, existingDuration, onDurationDetected } = {}
) => {
  const audioRef = useRef(null);
  const hiddenAudioRef = useRef(null);
  const waveContainerRef = useRef(null);
  const wsRef = useRef(null);
  const regionsPluginRef = useRef(null);
  const onDurationDetectedRef = useRef(onDurationDetected);
  const durationReportedRef = useRef(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [waveReady, setWaveReady] = useState(false);
  const [waveError, setWaveError] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  onDurationDetectedRef.current = onDurationDetected;

  useEffect(() => {
    durationReportedRef.current = false;
  }, [recordingFile, recordingId]);

  const formatTsDisplay = (s) => {
    if (s == null || Number.isNaN(s)) return "00:00:000";
    const totalMs = Math.round(s * 1000);
    const m = Math.floor(totalMs / 60000);
    const sec = Math.floor((totalMs % 60000) / 1000);
    const ms = totalMs % 1000;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}:${String(ms).padStart(
      3,
      "0"
    )}`;
  };

  useEffect(() => {
    if (!waveContainerRef.current || !hiddenAudioRef.current) return undefined;

    setWaveReady(false);
    setWaveError(null);

    const regionsPlugin = RegionsPlugin.create();
    const ws = WaveSurfer.create({
      container: waveContainerRef.current,
      media: hiddenAudioRef.current,
      waveColor: "#bdbdbd",
      progressColor: "#1976d2",
      cursorColor: "#1565c0",
      height: 60,
      barWidth: 2,
      barGap: 1,
      barRadius: 1,
      normalize: true,
      plugins: [regionsPlugin],
    });

    const onPlay = () => setIsAudioPlaying(true);
    const onPause = () => setIsAudioPlaying(false);
    const onFinish = () => setIsAudioPlaying(false);
    const onReady = () => {
      setWaveReady(true);
      const detected = ws.getDuration() || 0;
      setDuration(detected);
      if (
        detected > 0 &&
        !durationReportedRef.current &&
        shouldRefreshDuration(existingDuration, detected) &&
        recordingId &&
        onDurationDetectedRef.current
      ) {
        durationReportedRef.current = true;
        onDurationDetectedRef.current(recordingId, detected);
      }
    };
    const onTimeUpdate = (t) => setCurrentTime(t || 0);
    const onError = (err) => {
      console.error("WaveSurfer error:", err);
      setWaveError(String(err?.message || err));
    };

    ws.on("play", onPlay);
    ws.on("pause", onPause);
    ws.on("finish", onFinish);
    ws.on("ready", onReady);
    ws.on("timeupdate", onTimeUpdate);
    ws.on("error", onError);

    wsRef.current = ws;
    regionsPluginRef.current = regionsPlugin;
    audioRef.current = {
      get currentTime() {
        return ws.getCurrentTime();
      },
      pause: () => ws.pause(),
    };

    return () => {
      wsRef.current = null;
      regionsPluginRef.current = null;
      audioRef.current = null;
      setWaveReady(false);
      setIsAudioPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      ws.destroy();
    };
  }, [recordingFile, recordingId, existingDuration]);

  const parseTs = (ts) => {
    if (ts == null || ts === "") return null;
    const parts = String(ts).split(":").map(Number);
    if (parts.some(Number.isNaN)) return null;
    const [m, s, ms] = parts;
    return (m || 0) * 60 + (s || 0) + (ms || 0) / 1000;
  };

  useEffect(() => {
    if (!waveReady || !regionsPluginRef.current) return;
    const regions = regionsPluginRef.current;
    regions.clearRegions();
    (audioTimestamps || []).forEach((ts, i) => {
      const start = parseTs(ts?.start);
      const end = parseTs(ts?.end);
      const label = `${i + 1}`;
      if (start != null && end != null && end > start) {
        regions.addRegion({
          start,
          end,
          content: label,
          color: "rgba(25, 118, 210, 0.18)",
          drag: false,
          resize: false,
        });
      } else if (start != null) {
        regions.addRegion({
          start,
          end: start + 0.05,
          content: `${label}▶`,
          color: "rgba(34, 197, 94, 0.55)",
          drag: false,
          resize: false,
        });
      } else if (end != null) {
        regions.addRegion({
          start: Math.max(0, end - 0.05),
          end,
          content: `${label}◀`,
          color: "rgba(239, 68, 68, 0.55)",
          drag: false,
          resize: false,
        });
      }
    });
  }, [audioTimestamps, waveReady]);

  const togglePlay = () => {
    if (wsRef.current) {
      if (wsRef.current.isPlaying()) wsRef.current.pause();
      else wsRef.current.play();
      return;
    }
    if (hiddenAudioRef.current) {
      if (hiddenAudioRef.current.paused) hiddenAudioRef.current.play();
      else hiddenAudioRef.current.pause();
    }
  };

  const renderWaveform = (sticky = false) => (
    <Box
      mb={sticky ? 0 : 2}
      sx={
        sticky
          ? {
              position: "sticky",
              top: 0,
              zIndex: 5,
              bgcolor: "#fff",
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }
          : { display: "flex", alignItems: "center", gap: 1.5, width: "100%" }
      }
    >
      <IconButton
        onClick={togglePlay}
        color="primary"
        aria-label={isAudioPlaying ? "pause" : "play"}
        sx={{ border: "1px solid #1976d2", "&.Mui-disabled": { borderColor: "#bbb" } }}
      >
        {isAudioPlaying ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          ref={waveContainerRef}
          sx={{ width: "100%", minHeight: 60, bgcolor: "#fafafa", borderRadius: 1 }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 0.5,
            minHeight: 18,
          }}
        >
          <Box sx={{ flex: 1 }}>
            {!waveReady && !waveError && (
              <Typography variant="caption" color="text.secondary">
                Loading waveform…
              </Typography>
            )}
            {waveError && (
              <Typography variant="caption" color="error">
                Waveform unavailable. Audio still playable.
              </Typography>
            )}
          </Box>
          {waveReady && (
            <Typography
              variant="caption"
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                bgcolor: "rgba(15, 23, 42, 0.7)",
                color: "#fff",
                fontVariantNumeric: "tabular-nums",
                fontSize: "0.72rem",
                letterSpacing: 0.3,
                pointerEvents: "none",
              }}
            >
              {formatTsDisplay(currentTime)} /{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {formatTsDisplay(duration)}
              </Box>
            </Typography>
          )}
        </Box>
      </Box>
      <audio
        ref={hiddenAudioRef}
        src={proxiedAudioUrl(recordingFile)}
        crossOrigin="anonymous"
        preload="auto"
        style={{ display: "none" }}
      />
    </Box>
  );

  return { audioRef, renderWaveform };
};

// --- Annotation Row Component ---
const AnnotationRow = ({
  row,
  annotatorId,
  onAnnotationChange,
  onDurationDetected,
  canAnnotate,
  savingRecord,
  onSaveRecord,
  isExamSubmitted,
  onViewRemark,
  index,
  showMistakes = false,
  showDisfluency = false,
  showFluidity = false,
  showTimestamps = false,
  showValidationErrors = false,
}) => {
  const { t } = useTranslation();
  const readCorrectOptions = useMemo(() => getReadCorrectOptions(t), [t]);
  const fluidityOptions = useMemo(() => getFluidityOptions(t), [t]);
  const bgNoiseOptions = useMemo(() => getBgNoiseOptions(t), [t]);
  const notTranscribableOptions = useMemo(() => getNotTranscribableOptions(t), [t]);
  const phSelect = useMemo(() => getPlaceholder("select", t), [t]);
  const phReason = useMemo(() => getPlaceholder("selectReason", t), [t]);
  const phCorrect = useMemo(() => getPlaceholder("selectCorrect", t), [t]);

  const disabled = !canAnnotate(row) || isExamSubmitted;
  const isMarkedNonTranscribable = isNonTranscribable(row.annotation.not_transcribable);
  const fieldsDisabledByNonTranscribable = disabled || isMarkedNonTranscribable;
  const isPassage = isPassageSample(row);
  const isLongSplitPassage = isLongPassageRecording(row);
  const recordingDuration = Number(row.duration) || 0;
  const splitTimes = row.annotation.split_times || [];
  const segments = useMemo(
    () => syncSegmentData(splitTimes, recordingDuration, row.annotation.segments || []),
    [splitTimes, recordingDuration, row.annotation.segments]
  );
  const rowSupportsMistakes = String(row.is_no_mistakes ?? "0") === "1" || isPassage;
  const rowSupportsDisfluency = String(row.is_disfluency ?? "0") === "1" || isPassage;
  const rowSupportsFluidity = isFluiditySampleType(row?.sample_type);
  const maxMistakes = useMemo(() => getMaxMistakesForRecording(row), [row]);
  const tableRowSx = useMemo(
    () => buildTableRowSx(showMistakes, showFluidity, showDisfluency, showTimestamps),
    [showMistakes, showFluidity, showDisfluency, showTimestamps]
  );

  const canonicalLines = useMemo(
    () => splitTextIntoLines(row.sample_text, isPassage),
    [row.sample_text, isPassage]
  );

  const transcriptionLines = useMemo(() => {
    const lines = row.annotation.transcription_lines || [];
    const diff = canonicalLines.length - lines.length;
    if (diff > 0) return [...lines, ...Array(diff).fill("")]; // Use empty strings
    return lines.slice(0, canonicalLines.length);
  }, [row.annotation.transcription_lines, canonicalLines]);

  const audioTimestamps = useMemo(() => {
    const ts = row.annotation.audio_timestamps || [];
    const normalized = ts.map((it) =>
      it && typeof it === "object" ? it : { start: null, end: null }
    );
    const diff = canonicalLines.length - normalized.length;
    if (diff > 0) {
      return [...normalized, ...Array.from({ length: diff }, () => ({ start: null, end: null }))];
    }
    return normalized.slice(0, canonicalLines.length);
  }, [row.annotation.audio_timestamps, canonicalLines]);

  const { audioRef, renderWaveform } = useRecordingWaveform(row.recording_file, audioTimestamps, {
    recordingId: row.recording_id,
    existingDuration: row.duration,
    onDurationDetected,
  });

  const formatTs = (s) => {
    if (s == null) return null;
    const totalMs = Math.round(s * 1000);
    const m = Math.floor(totalMs / 60000);
    const sec = Math.floor((totalMs % 60000) / 1000);
    const ms = totalMs % 1000;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}:${String(ms).padStart(
      3,
      "0"
    )}`;
  };

  const handleMarkStart = (lineIndex) => {
    if (fieldsDisabledByNonTranscribable || !audioRef.current) return;
    const current = audioRef.current.currentTime;
    const updated = audioTimestamps.map((it, i) =>
      i === lineIndex ? { ...(it || {}), start: formatTs(current) } : it
    );
    onAnnotationChange(row.recording_id, "audio_timestamps", updated);
  };

  const handleMarkEnd = (lineIndex) => {
    if (fieldsDisabledByNonTranscribable || !audioRef.current) return;
    const current = audioRef.current.currentTime;
    const updated = audioTimestamps.map((it, i) =>
      i === lineIndex ? { ...(it || {}), end: formatTs(current) } : it
    );
    onAnnotationChange(row.recording_id, "audio_timestamps", updated);
    audioRef.current.pause();
  };

  const handleClearStart = (lineIndex) => {
    if (fieldsDisabledByNonTranscribable) return;
    const updated = audioTimestamps.map((it, i) =>
      i === lineIndex ? { ...(it || {}), start: null } : it
    );
    onAnnotationChange(row.recording_id, "audio_timestamps", updated);
  };

  const handleClearEnd = (lineIndex) => {
    if (fieldsDisabledByNonTranscribable) return;
    const updated = audioTimestamps.map((it, i) =>
      i === lineIndex ? { ...(it || {}), end: null } : it
    );
    onAnnotationChange(row.recording_id, "audio_timestamps", updated);
  };

  // Function to handle copy from canonical to transcription
  const handleCopyToTranscription = (lineIndex) => {
    if (fieldsDisabledByNonTranscribable) return;

    const canonicalText = canonicalLines[lineIndex];
    if (!canonicalText) return;

    const updatedTranscriptionLines = [...transcriptionLines];
    updatedTranscriptionLines[lineIndex] = canonicalText;

    onAnnotationChange(row.recording_id, "transcription_lines", updatedTranscriptionLines);
  };

  // Function to copy all canonical text to all transcription lines
  const handleCopyAllToTranscription = () => {
    if (fieldsDisabledByNonTranscribable) return;

    const updatedTranscriptionLines = canonicalLines.map((line) => line);
    onAnnotationChange(row.recording_id, "transcription_lines", updatedTranscriptionLines);
  };

  const passageMetaGridSize = { xs: 6, sm: 4, md: 3, lg: 2 };

  const renderPassageMistakesField = () => {
    if (!rowSupportsMistakes) return null;
    return (
      <Grid item {...passageMetaGridSize}>
        <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
          {t("annotator.annotationPage.columns.noOfMistakes", "No. of mistakes")}
        </Typography>
        <AnnotationTextField
          fullWidth
          size="small"
          type="number"
          inputProps={{ min: 0, max: maxMistakes }}
          value={row.annotation.no_of_mistakes ?? 0}
          disabled={fieldsDisabledByNonTranscribable}
          error={
            showValidationErrors &&
            !isMarkedNonTranscribable &&
            Number(row.annotation.no_of_mistakes ?? 0) > maxMistakes
          }
          onChange={(e) => {
            const raw = e.target.value === "" ? "" : Number(e.target.value);
            const next = raw === "" ? "" : Math.min(Math.max(0, raw), maxMistakes);
            onAnnotationChange(row.recording_id, "no_of_mistakes", next);
          }}
        />
      </Grid>
    );
  };

  const renderPassageDisfluencyField = () => {
    if (!rowSupportsDisfluency) return null;
    return (
      <Grid item {...passageMetaGridSize}>
        <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
          {t("annotator.annotationPage.columns.disfluencyOccurrences", "Disfluency occurrences")}
        </Typography>
        <AnnotationTextField
          fullWidth
          size="small"
          type="number"
          inputProps={{ min: 0 }}
          value={row.annotation.disfluency_occurences ?? 0}
          disabled={fieldsDisabledByNonTranscribable}
          onChange={(e) =>
            onAnnotationChange(
              row.recording_id,
              "disfluency_occurences",
              e.target.value === "" ? "" : Number(e.target.value)
            )
          }
        />
      </Grid>
    );
  };

  /* ================= LONG PASSAGE SPLIT LAYOUT ================= */
  if (isPassage && isLongSplitPassage) {
    const handleSegmentChange = (segIdx, key, value) => {
      const updated = segments.map((seg, i) => (i === segIdx ? { ...seg, [key]: value } : seg));
      onAnnotationChange(row.recording_id, "segments", updated);
    };

    const renderPassageMetaFields = () => (
      <Grid container spacing={2} mt={2} alignItems="center">
        <Grid item {...passageMetaGridSize}>
          <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
            {t("annotator.annotationPage.columns.nonTranscribable", "Non-transcribable")}
          </Typography>
          <SelectField
            field="not_transcribable"
            options={notTranscribableOptions}
            placeholder={phReason}
            selectedValue={row.annotation.not_transcribable}
            onChange={(e) =>
              onAnnotationChange(row.recording_id, "not_transcribable", e.target.value)
            }
            disabled={disabled}
          />
        </Grid>
        <Grid item {...passageMetaGridSize}>
          <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
            {t("annotator.annotationPage.columns.readCorrectlyShort", "Read correctly")}
          </Typography>
          <SelectField
            field="read_correctly"
            options={readCorrectOptions}
            placeholder={phSelect}
            selectedValue={resolveReadCorrectlySelectedValue(row.annotation.read_correctly)}
            onChange={(e) =>
              onAnnotationChange(row.recording_id, "read_correctly", e.target.value === "Yes")
            }
            disabled={fieldsDisabledByNonTranscribable}
          />
        </Grid>
        <Grid item {...passageMetaGridSize}>
          <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
            {t("annotator.annotationPage.columns.bgNoise", "BG noise")}
          </Typography>
          <SelectField
            field="bg_noise"
            options={bgNoiseOptions}
            placeholder={phSelect}
            selectedValue={row.annotation.bg_noise}
            onChange={(e) => onAnnotationChange(row.recording_id, "bg_noise", e.target.value)}
            disabled={fieldsDisabledByNonTranscribable}
          />
        </Grid>
        {renderPassageMistakesField()}
        {renderPassageDisfluencyField()}
        {rowSupportsFluidity && (
          <Grid item {...passageMetaGridSize}>
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              {t("annotator.annotationPage.columns.fluidity", "Fluidity")}
            </Typography>
            <SelectField
              field="fluidity"
              options={fluidityOptions}
              placeholder={phSelect}
              selectedValue={resolveFluiditySelectedValue(row.annotation.fluidity)}
              onChange={(e) =>
                onAnnotationChange(row.recording_id, "fluidity", e.target.value === "Yes")
              }
              disabled={fieldsDisabledByNonTranscribable}
            />
          </Grid>
        )}
        <Grid item xs="auto">
          <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
            &nbsp;
          </Typography>
          <IconButton
            onClick={() => onSaveRecord(row.recording_id)}
            disabled={savingRecord[row.recording_id] || disabled}
          >
            {savingRecord[row.recording_id] ? <CircularProgress size={18} /> : <SaveIcon />}
          </IconButton>
        </Grid>
        {row.annotation.remarks && (
          <Grid item xs={2} display="flex" alignItems="center">
            <MDButton
              size="small"
              color="info"
              variant="outlined"
              startIcon={<CommentIcon fontSize="small" />}
              onClick={() => onViewRemark(row.annotation.remarks)}
              sx={{ whiteSpace: "nowrap", mt: 2.5 }}
            >
              {t("annotator.annotationPage.buttons.viewRemark", "View remark")}
            </MDButton>
          </Grid>
        )}
      </Grid>
    );

    return (
      <Box py={2} borderBottom="1px solid #eee">
        <SplitAnnotationPlayer
          src={row.recording_file}
          duration={recordingDuration}
          splitTimes={splitTimes}
          onSplitTimesChange={(next) => onAnnotationChange(row.recording_id, "split_times", next)}
          disabled={fieldsDisabledByNonTranscribable}
        />
        <Box sx={{ maxHeight: "50vh", overflowY: "auto", pr: 1 }}>
          <SegmentAnnotationFields
            referenceText={row.sample_text || ""}
            splitTimes={splitTimes}
            duration={recordingDuration}
            segments={segments}
            onSegmentChange={handleSegmentChange}
            disabled={fieldsDisabledByNonTranscribable}
            t={t}
          />
        </Box>
        {renderPassageMetaFields()}
      </Box>
    );
  }

  /* ================= PASSAGE LAYOUT ================= */
  if (isPassage) {
    return (
      <Box py={2} borderBottom="1px solid #eee">
        {renderWaveform(true)}

        <Box sx={{ maxHeight: "50vh", overflowY: "auto", pr: 1 }}>
          {canonicalLines.map((canonicalLine, lineIndex) => (
            <Grid container spacing={2} key={lineIndex} alignItems="center" sx={{ mb: 1.5 }}>
              <Grid item xs={showTimestamps ? 5 : 6} sx={{ position: "relative" }}>
                <TextField
                  fullWidth
                  value={canonicalLine}
                  size="small"
                  multiline
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <CopyButton
                        onClick={() => handleCopyToTranscription(lineIndex)}
                        disabled={fieldsDisabledByNonTranscribable}
                        title={t(
                          "annotator.annotationPage.copy.toTranscriptionSentence",
                          "Copy this sentence to transcription"
                        )}
                      />
                    ),
                    sx: { pr: 6 }, // Add padding for the copy button
                  }}
                  sx={{ bgcolor: "#f7f8f9" }}
                />
              </Grid>

              <Grid item xs={showTimestamps ? 4.5 : 6} sx={{ minWidth: 0 }}>
                <AnnotationTextField
                  fullWidth
                  value={
                    transcriptionLines[lineIndex] === "-" ? "" : transcriptionLines[lineIndex] || ""
                  }
                  size="small"
                  multiline
                  placeholder={t(
                    "annotator.annotationPage.placeholders.sentence",
                    "Sentence {{n}}"
                  ).replace("{{n}}", String(lineIndex + 1))}
                  disabled={fieldsDisabledByNonTranscribable}
                  error={
                    showValidationErrors &&
                    !isMarkedNonTranscribable &&
                    String(
                      transcriptionLines[lineIndex] === "-"
                        ? ""
                        : transcriptionLines[lineIndex] || ""
                    ).trim() === ""
                  }
                  onChange={(e) => {
                    const updated = [...transcriptionLines];
                    updated[lineIndex] = e.target.value;
                    onAnnotationChange(row.recording_id, "transcription_lines", updated);
                  }}
                />
              </Grid>

              {showTimestamps && (
                <Grid item xs={2.5} sx={{ minWidth: 0 }}>
                  <LineTimestampControls
                    lineIndex={lineIndex}
                    audioTimestamps={audioTimestamps}
                    disabled={fieldsDisabledByNonTranscribable}
                    onMarkStart={handleMarkStart}
                    onMarkEnd={handleMarkEnd}
                    onClearStart={handleClearStart}
                    onClearEnd={handleClearEnd}
                  />
                </Grid>
              )}
            </Grid>
          ))}
        </Box>

        {/* Meta fields */}
        <Grid container spacing={2} mt={2} alignItems="center">
          {/* Non-Transcribable */}
          <Grid item {...passageMetaGridSize}>
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              {t("annotator.annotationPage.columns.nonTranscribable", "Non-transcribable")}
            </Typography>
            <SelectField
              field="not_transcribable"
              options={notTranscribableOptions}
              placeholder={phReason}
              selectedValue={row.annotation.not_transcribable}
              onChange={(e) =>
                onAnnotationChange(row.recording_id, "not_transcribable", e.target.value)
              }
              disabled={disabled}
            />
          </Grid>

          {/* Read Correctly */}
          <Grid item {...passageMetaGridSize}>
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              {t("annotator.annotationPage.columns.readCorrectlyShort", "Read correctly")}
            </Typography>
            <SelectField
              field="read_correctly"
              options={readCorrectOptions}
              placeholder={phSelect}
              selectedValue={resolveReadCorrectlySelectedValue(row.annotation.read_correctly)}
              onChange={(e) =>
                onAnnotationChange(row.recording_id, "read_correctly", e.target.value === "Yes")
              }
              disabled={fieldsDisabledByNonTranscribable}
            />
          </Grid>

          {/* BG Noise */}
          <Grid item {...passageMetaGridSize}>
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              {t("annotator.annotationPage.columns.bgNoise", "BG noise")}
            </Typography>
            <SelectField
              field="bg_noise"
              options={bgNoiseOptions}
              placeholder={phSelect}
              selectedValue={row.annotation.bg_noise}
              onChange={(e) => onAnnotationChange(row.recording_id, "bg_noise", e.target.value)}
              disabled={fieldsDisabledByNonTranscribable}
            />
          </Grid>

          {renderPassageMistakesField()}
          {renderPassageDisfluencyField()}

          {/* Fluidity */}
          {rowSupportsFluidity && (
            <Grid item {...passageMetaGridSize}>
              <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
                {t("annotator.annotationPage.columns.fluidity", "Fluidity")}
              </Typography>
              <SelectField
                field="fluidity"
                options={fluidityOptions}
                placeholder={phSelect}
                selectedValue={resolveFluiditySelectedValue(row.annotation.fluidity)}
                onChange={(e) =>
                  onAnnotationChange(row.recording_id, "fluidity", e.target.value === "Yes")
                }
                disabled={fieldsDisabledByNonTranscribable}
              />
            </Grid>
          )}

          {/* Save */}
          <Grid item xs="auto">
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              &nbsp;
            </Typography>
            <IconButton
              onClick={() => onSaveRecord(row.recording_id)}
              disabled={savingRecord[row.recording_id] || disabled}
            >
              {savingRecord[row.recording_id] ? <CircularProgress size={18} /> : <SaveIcon />}
            </IconButton>
          </Grid>

          {/* View Remark */}
          {row.annotation.remarks && (
            <Grid item xs={2} display="flex" alignItems="center">
              <MDButton
                size="small"
                color="info"
                variant="outlined"
                startIcon={<CommentIcon fontSize="small" />}
                onClick={() => onViewRemark(row.annotation.remarks)}
                sx={{ whiteSpace: "nowrap", mt: 2.5 }}
              >
                {t("annotator.annotationPage.buttons.viewRemark", "View remark")}
              </MDButton>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  }

  /* ================= NON-PASSAGE (TABLE) ================= */
  return (
    <Box
      sx={{
        ...tableRowSx,
        py: 1,
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      <Box>{renderWaveform(false)}</Box>

      <Box>
        {canonicalLines.map((canonicalLine, lineIndex) => (
          <Box key={lineIndex} sx={{ position: "relative", mb: 0.5 }}>
            <TextField
              value={canonicalLine}
              fullWidth
              size="small"
              multiline
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <CopyButton
                    onClick={() => handleCopyToTranscription(lineIndex)}
                    disabled={fieldsDisabledByNonTranscribable}
                    title={t(
                      "annotator.annotationPage.copy.toTranscriptionLine",
                      "Copy this line to transcription"
                    )}
                  />
                ),
                sx: { pr: 6 },
              }}
              sx={{ bgcolor: "#f7f8f9" }}
            />
          </Box>
        ))}
        {canonicalLines.length > 1 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyAllToTranscription}
            disabled={fieldsDisabledByNonTranscribable}
            sx={{
              fontSize: "0.75rem",
              mt: 0.5,
              ...DISABLED_BUTTON_SX,
            }}
            fullWidth
          >
            {t("annotator.annotationPage.copy.allCanonical", "Copy all")}
          </Button>
        )}
      </Box>

      <Box>
        <SelectField
          field="not_transcribable"
          options={notTranscribableOptions}
          placeholder={phReason}
          selectedValue={row.annotation.not_transcribable}
          onChange={(e) =>
            onAnnotationChange(row.recording_id, "not_transcribable", e.target.value)
          }
          disabled={disabled}
        />
      </Box>

      <Box>
        {transcriptionLines.map((line, lineIndex) => (
          <AnnotationTextField
            key={lineIndex}
            value={line}
            fullWidth
            size="small"
            multiline
            disabled={fieldsDisabledByNonTranscribable}
            error={
              showValidationErrors && !isMarkedNonTranscribable && String(line || "").trim() === ""
            }
            sx={{ mb: 0.5 }}
            onChange={(e) => {
              const updated = [...transcriptionLines];
              updated[lineIndex] = e.target.value;
              onAnnotationChange(row.recording_id, "transcription_lines", updated);
            }}
          />
        ))}
      </Box>

      {showTimestamps && (
        <Box sx={{ minWidth: 0 }}>
          {canonicalLines.map((_, lineIndex) => (
            <LineTimestampControls
              key={lineIndex}
              lineIndex={lineIndex}
              audioTimestamps={audioTimestamps}
              disabled={fieldsDisabledByNonTranscribable}
              onMarkStart={handleMarkStart}
              onMarkEnd={handleMarkEnd}
              onClearStart={handleClearStart}
              onClearEnd={handleClearEnd}
            />
          ))}
        </Box>
      )}

      <Box>
        <SelectField
          field="read_correctly"
          options={readCorrectOptions}
          placeholder={phCorrect}
          selectedValue={resolveReadCorrectlySelectedValue(row.annotation.read_correctly)}
          onChange={(e) =>
            onAnnotationChange(row.recording_id, "read_correctly", e.target.value === "Yes")
          }
          disabled={fieldsDisabledByNonTranscribable}
        />
      </Box>

      {showMistakes && (
        <Box>
          <AnnotationTextField
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: 0, max: maxMistakes }}
            value={row.annotation.no_of_mistakes ?? 0}
            disabled={fieldsDisabledByNonTranscribable || !rowSupportsMistakes}
            error={
              showValidationErrors &&
              rowSupportsMistakes &&
              !isMarkedNonTranscribable &&
              Number(row.annotation.no_of_mistakes ?? 0) > maxMistakes
            }
            onChange={(e) => {
              const raw = e.target.value === "" ? "" : Number(e.target.value);
              const next = raw === "" ? "" : Math.min(Math.max(0, raw), maxMistakes);
              onAnnotationChange(row.recording_id, "no_of_mistakes", next);
            }}
          />
        </Box>
      )}

      <Box>
        <SelectField
          field="bg_noise"
          options={bgNoiseOptions}
          placeholder={phSelect}
          selectedValue={row.annotation.bg_noise}
          onChange={(e) => onAnnotationChange(row.recording_id, "bg_noise", e.target.value)}
          disabled={fieldsDisabledByNonTranscribable}
        />
      </Box>

      {showFluidity && (
        <Box>
          <SelectField
            field="fluidity"
            options={fluidityOptions}
            placeholder={phSelect}
            selectedValue={resolveFluiditySelectedValue(row.annotation.fluidity)}
            onChange={(e) =>
              onAnnotationChange(row.recording_id, "fluidity", e.target.value === "Yes")
            }
            disabled={fieldsDisabledByNonTranscribable || !rowSupportsFluidity}
          />
        </Box>
      )}

      {showDisfluency && (
        <Box>
          <AnnotationTextField
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: 0 }}
            value={row.annotation.disfluency_occurences ?? 0}
            disabled={fieldsDisabledByNonTranscribable || !rowSupportsDisfluency}
            onChange={(e) =>
              onAnnotationChange(
                row.recording_id,
                "disfluency_occurences",
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
          />
        </Box>
      )}

      <Box>
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
          <IconButton
            onClick={() => onSaveRecord(row.recording_id)}
            disabled={savingRecord[row.recording_id] || disabled}
          >
            {savingRecord[row.recording_id] ? <CircularProgress size={16} /> : <SaveIcon />}
          </IconButton>
          {row.annotation.remarks && (
            <MDButton
              size="small"
              color="info"
              variant="outlined"
              startIcon={<CommentIcon fontSize="small" />}
              onClick={() => onViewRemark(row.annotation.remarks)}
              sx={{ whiteSpace: "nowrap" }}
            >
              {t("annotator.annotationPage.buttons.viewRemark", "View remark")}
            </MDButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};

AnnotationRow.propTypes = {
  row: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  annotatorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAnnotationChange: PropTypes.func.isRequired,
  onDurationDetected: PropTypes.func,
  canAnnotate: PropTypes.func.isRequired,
  savingRecord: PropTypes.object.isRequired,
  onSaveRecord: PropTypes.func.isRequired,
  isExamSubmitted: PropTypes.bool.isRequired,
  onViewRemark: PropTypes.func.isRequired,
  showMistakes: PropTypes.bool,
  showDisfluency: PropTypes.bool,
  showFluidity: PropTypes.bool,
  showTimestamps: PropTypes.bool,
  showValidationErrors: PropTypes.bool,
};

// --- Main Annotation Page Component ---

export default function AnnotationPage() {
  const query = useQuery();
  const navigate = useNavigate();
  const examId = query.get("exam_id") || "";
  const { t } = useTranslation();
  const translateSampleType = useCallback((label) => translateSampleTypeLabel(label, t), [t]);

  const [loading, setLoading] = useState(false);
  const [exam, setExam] = useState(null);
  const [tabs, setTabs] = useState({});
  const [sampleTypes, setSampleTypes] = useState([]);
  const [activeType, setActiveType] = useState("");

  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [savingRecord, setSavingRecord] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
  const [remarkDialogContent, setRemarkDialogContent] = useState("");
  const [validationHighlightIds, setValidationHighlightIds] = useState({});

  // Navigation state
  const [adjacentExams, setAdjacentExams] = useState({
    next_exam: null,
    prev_exam: null,
    current_exam: null,
    position: 0,
    total: 0,
  });
  const [loadingAdjacent, setLoadingAdjacent] = useState(false);

  // snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    title: "",
    message: "",
  });

  const annotatorId = getLoggedInAnnotatorId();

  const [groupedRecordings, setGroupedRecordings] = useState({});

  const loadGenerationRef = useRef(0);
  const examIdRef = useRef(examId);
  examIdRef.current = examId;

  const studentId = exam?.student_id || "";
  const language = tabs[sampleTypes[0]]?.[0]?.sample_language || "";

  const myExamStatus = useMemo(() => {
    if (!exam || !annotatorId) return "";

    const isA1 = String(exam.annotator1_id ?? "") === String(annotatorId);
    const isA2 = String(exam.annotator2_id ?? "") === String(annotatorId);

    if (isA1) return String(exam.annotator1_status || "").toLowerCase();
    if (isA2) return String(exam.annotator2_status || "").toLowerCase();

    return "";
  }, [exam, annotatorId]);

  const myAnnotatorSlot = useMemo(() => {
    if (!exam || !annotatorId) return null;
    if (String(exam.annotator1_id ?? "") === String(annotatorId)) return 1;
    if (String(exam.annotator2_id ?? "") === String(annotatorId)) return 2;
    return null;
  }, [exam, annotatorId]);

  const myQaStatus = useMemo(() => {
    if (myAnnotatorSlot === 1) return exam?.qa1_status || "";
    if (myAnnotatorSlot === 2) return exam?.qa2_status || "";
    return exam?.qa_status || "";
  }, [exam, myAnnotatorSlot]);

  const myQaComment = useMemo(() => {
    if (myAnnotatorSlot === 1) {
      return exam?.qa1_comment || "";
    }
    if (myAnnotatorSlot === 2) {
      return exam?.qa2_comment || "";
    }
    return exam?.qa_comment || "";
  }, [exam, myAnnotatorSlot]);

  const isMyExamCompleted = myExamStatus === "submitted" || myExamStatus === "completed";
  const isRejectedByQa = myQaStatus === "Rejected";
  const isExamSubmitted = isMyExamCompleted && !isRejectedByQa;

  const isSavingAnyRecord = useMemo(
    () => Object.values(savingRecord).some(Boolean),
    [savingRecord]
  );

  // snackbar helpers
  const showSnackbar = useCallback(
    ({ severity = "success", title = "", message = "", duration = 3000 }) => {
      setSnackbar({ open: true, severity, title, message });
      if (duration > 0) {
        setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), duration);
      }
    },
    []
  );

  const closeSnackbar = useCallback(() => setSnackbar((s) => ({ ...s, open: false })), []);

  const handleViewRemark = useCallback((remarkText) => {
    setRemarkDialogContent(remarkText || "");
    setRemarkDialogOpen(true);
  }, []);

  // Helper to check if annotator assigned
  const canAnnotate = useCallback(
    (rec) => {
      if (!rec) return false;
      const isA1 = String(rec.annotator1_id ?? "") === String(annotatorId);
      const isA2 = String(rec.annotator2_id ?? "") === String(annotatorId);
      return isA1 || isA2;
    },
    [annotatorId]
  );

  // Flatten and init annotations
  const processExamBundle = useCallback(
    async (bundle, generation) => {
      const newGroupedRecordings = {};

      for (const sampleType of bundle.sample_types) {
        const currentGroups = bundle.tabs[sampleType] || [];
        const recordingsWithSampleText = currentGroups.flatMap((group) =>
          group.recordings.map((rec) => {
            const isPassage =
              isPassageSample({ ...rec, sample_type: sampleType }) ||
              rec.is_passage === 1 ||
              rec.is_passage === "1" ||
              group.is_passage === 1 ||
              group.is_passage === "1" ||
              sampleType === "Passage";
            const sampleTextLines = splitTextIntoLines(group.sample_text, isPassage);
            const recordingDuration = Number(rec.duration) || 0;
            const isLongPassage = isPassage && recordingDuration > SEG_LIMIT;
            const defaultSplitTimes = isLongPassage
              ? generateDefaultSplitTimes(recordingDuration)
              : [];
            const defaultSegments = isLongPassage
              ? syncSegmentData(defaultSplitTimes, recordingDuration, [])
              : [];

            return {
              ...rec,
              is_disfluency: rec.is_disfluency ?? group.is_disfluency ?? 0,
              is_no_mistakes: rec.is_no_mistakes ?? group.is_no_mistakes ?? 1,
              is_passage: isPassage ? 1 : 0,
              sample_text: group.sample_text,
              sample_type: sampleType,
              annotation: {
                transcription_text: "",
                transcription_lines: sampleTextLines.map(() => ""),
                audio_timestamps: sampleTextLines.map(() => ({ start: null, end: null })),
                split_times: defaultSplitTimes,
                segments: defaultSegments,
                read_correctly: false,
                bg_noise: "",
                not_transcribable: "",
                disfluency_occurences: 0,
                no_of_mistakes: 0,
                fluidity: null,
              },
            };
          })
        );

        const annotationPromises = recordingsWithSampleText.map(async (rec) => {
          try {
            const existingAnnotation = await getAnnotation(rec.recording_id, annotatorId);
            if (existingAnnotation?.status === "success" && existingAnnotation.data) {
              const data = existingAnnotation.data;
              const isPassage = isPassageSample(rec);
              const recordingDuration = Number(rec.duration) || 0;
              const isLongPassage = isPassage && recordingDuration > SEG_LIMIT;

              let splitTimes = [];
              let segments = [];
              if (isLongPassage) {
                if (data.split_times) {
                  try {
                    splitTimes =
                      typeof data.split_times === "string"
                        ? JSON.parse(data.split_times)
                        : data.split_times;
                  } catch {
                    splitTimes = [];
                  }
                }
                if (!Array.isArray(splitTimes) || splitTimes.length === 0) {
                  splitTimes = generateDefaultSplitTimes(recordingDuration);
                }

                if (Array.isArray(data.segments) && data.segments.length > 0) {
                  segments = data.segments.map((s) => ({
                    reference_text: s.reference_text || "",
                    transcription_text: s.transcription_text || "",
                  }));
                }
                segments = syncSegmentData(splitTimes, recordingDuration, segments);
              }

              const canonicalTextLines = splitTextIntoLines(rec.sample_text, isPassage);
              const canonicalLineCount = canonicalTextLines.length;

              let transcriptionLines;

              if (isLongPassage && segments.length > 0) {
                transcriptionLines = segments.map((s) => s.transcription_text || "");
              } else if (data.transcription_text && data.transcription_text.trim() !== "") {
                transcriptionLines = data.transcription_text.split("\n");
                transcriptionLines = transcriptionLines.map((line) => line.trim());
              } else {
                transcriptionLines = Array(canonicalLineCount).fill("");
              }

              if (transcriptionLines.length < canonicalLineCount) {
                const missingLines = Array(canonicalLineCount - transcriptionLines.length).fill("");
                transcriptionLines = [...transcriptionLines, ...missingLines];
              } else if (transcriptionLines.length > canonicalLineCount) {
                transcriptionLines = transcriptionLines.slice(0, canonicalLineCount);
              }

              const audioTimestamps = parseAudioTimestampsFromDb(
                data.audio_timestamps,
                canonicalLineCount
              );

              return {
                ...rec,
                annotation: {
                  transcription_text: data.transcription_text || "",
                  transcription_lines: transcriptionLines,
                  audio_timestamps: audioTimestamps,
                  split_times: splitTimes,
                  segments,
                  read_correctly: Boolean(data.read_correctly),
                  bg_noise: data.bg_noise ?? "",
                  not_transcribable: data.not_transcribable ?? "",
                  disfluency_occurences:
                    data.disfluency_occurences ?? rec.annotation.disfluency_occurences ?? 0,
                  no_of_mistakes: data.no_of_mistakes ?? rec.annotation.no_of_mistakes ?? 0,
                  fluidity: data.fluidity ?? null,
                  remarks: data.remarks ?? "",
                },
              };
            }
          } catch (error) {
            console.error(`Failed to fetch annotation for ${rec.recording_id}:`, error);
          }
          return rec;
        });

        newGroupedRecordings[sampleType] = await Promise.all(annotationPromises);

        if (generation !== undefined && loadGenerationRef.current !== generation) {
          return;
        }
      }

      if (generation !== undefined && loadGenerationRef.current !== generation) {
        return;
      }

      setGroupedRecordings(newGroupedRecordings);
    },
    [annotatorId]
  );

  const refreshExamData = useCallback(
    async (targetExamId) => {
      const generation = loadGenerationRef.current;
      const bundle = await getExamBundle(targetExamId);

      if (loadGenerationRef.current !== generation || targetExamId !== examIdRef.current) {
        return false;
      }

      setExam(bundle.exam || null);
      setTabs(bundle.tabs || {});
      setSampleTypes(bundle.sample_types || []);
      await processExamBundle(bundle, generation);
      return loadGenerationRef.current === generation && targetExamId === examIdRef.current;
    },
    [processExamBundle]
  );

  const fetchAdjacentExamsForExam = useCallback(
    async (sourceExamId) => {
      if (!sourceExamId || !annotatorId) return null;

      return getAdjacentExamsWithFilter(sourceExamId, annotatorId, {});
    },
    [annotatorId]
  );

  // Function to get adjacent exams
  const fetchAdjacentExams = useCallback(async () => {
    if (!examId || !annotatorId) return;

    try {
      setLoadingAdjacent(true);
      const data = await fetchAdjacentExamsForExam(examId);
      if (data) setAdjacentExams(data);
    } catch (error) {
      console.error("Failed to fetch adjacent exams:", error);
      // Don't show error snackbar for navigation data - it's optional
    } finally {
      setLoadingAdjacent(false);
    }
  }, [examId, annotatorId, fetchAdjacentExamsForExam]);

  // Function to navigate to another exam
  const handleNavigateToExam = useCallback(
    (newExamId) => {
      if (!newExamId || String(newExamId) === String(examId)) return;

      const newSearchParams = new URLSearchParams(query.toString());
      newSearchParams.set("exam_id", newExamId);

      navigate(
        {
          pathname: window.location.pathname,
          search: `?${newSearchParams.toString()}`,
        },
        { replace: true }
      );
    },
    [examId, navigate, query]
  );

  const handlePreviousExam = useCallback(() => {
    if (adjacentExams.prev_exam?.exam_id) {
      handleNavigateToExam(adjacentExams.prev_exam.exam_id);
    }
  }, [adjacentExams.prev_exam, handleNavigateToExam]);

  const handleNextExam = useCallback(() => {
    if (adjacentExams.next_exam?.exam_id) {
      handleNavigateToExam(adjacentExams.next_exam.exam_id);
    }
  }, [adjacentExams.next_exam, handleNavigateToExam]);

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        return;
      }

      if (e.ctrlKey && e.key === "ArrowLeft") {
        e.preventDefault();
        handlePreviousExam();
      }

      if (e.ctrlKey && e.key === "ArrowRight") {
        e.preventDefault();
        handleNextExam();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handlePreviousExam, handleNextExam]);

  // Initial fetch for exam data (sync reset + generation guard on examId change)
  useEffect(() => {
    if (!examId) {
      setExam(null);
      setTabs({});
      setSampleTypes([]);
      setActiveType("");
      setGroupedRecordings({});
      setLoading(false);
      return;
    }

    loadGenerationRef.current += 1;
    const generation = loadGenerationRef.current;

    setLoading(true);
    setExam(null);
    setTabs({});
    setSampleTypes([]);
    setActiveType("");
    setGroupedRecordings({});
    setBulkSaving(false);
    setSavingRecord({});
    setMarkingComplete(false);
    setConfirmCompleteOpen(false);

    (async () => {
      try {
        const bundle = await getExamBundle(examId);
        if (loadGenerationRef.current !== generation) return;

        setExam(bundle.exam || null);
        setTabs(bundle.tabs || {});
        setSampleTypes(bundle.sample_types || []);
        setActiveType(bundle.sample_types?.[0] || "");
        await processExamBundle(bundle, generation);
      } catch (err) {
        if (loadGenerationRef.current !== generation) return;

        console.error("getExamBundle failed:", err);
        showSnackbar({
          severity: "error",
          title: t("annotator.annotationPage.snackbar.loadFailed", "Load failed"),
          message: t(
            "annotator.annotationPage.snackbar.loadExamFailed",
            "Failed to load exam data"
          ),
        });
        setExam(null);
        setTabs({});
        setSampleTypes([]);
        setActiveType("");
        setGroupedRecordings({});
      } finally {
        if (loadGenerationRef.current === generation) {
          setLoading(false);
        }
      }
    })();
  }, [examId, processExamBundle, showSnackbar, t]);

  // Fetch adjacent exams when examId or annotatorId changes
  useEffect(() => {
    fetchAdjacentExams();
  }, [examId, annotatorId, fetchAdjacentExams]);

  const activeRecordings = useMemo(
    () => groupedRecordings[activeType] || [],
    [groupedRecordings, activeType]
  );

  const hasAnyPassage = useMemo(
    () => activeRecordings.some((r) => isPassageSample(r)),
    [activeRecordings]
  );

  const hasDisfluency = useMemo(
    () => activeRecordings.some((r) => String(r?.is_disfluency ?? 0) === "1"),
    [activeRecordings]
  );
  const hasNoMistakes = useMemo(
    () => activeRecordings.some((r) => String(r?.is_no_mistakes ?? 0) === "1"),
    [activeRecordings]
  );
  const hasFluidity = useMemo(
    () => activeRecordings.some((r) => isFluiditySampleType(r?.sample_type)),
    [activeRecordings]
  );
  const hasTimestamps = useMemo(
    () => activeRecordings.some((r) => isFluiditySampleType(r?.sample_type)),
    [activeRecordings]
  );

  const getSectionStatus = (sampleType) => {
    const recs = groupedRecordings[sampleType] || [];
    if (!recs.length) return "pending";
    const lowerStatus = (s) => String(s || "").toLowerCase();
    const allBothSubmitted = recs.every(
      (r) =>
        lowerStatus(r.annotation1_status) === "submitted" &&
        lowerStatus(r.annotation2_status) === "submitted"
    );
    if (allBothSubmitted) return "completed";
    const anySubmitted = recs.some(
      (r) =>
        lowerStatus(r.annotation1_status) === "submitted" ||
        lowerStatus(r.annotation2_status) === "submitted"
    );
    if (anySubmitted) return "inProgress";
    return "pending";
  };

  // annotation change for a recording
  const handleDurationDetected = useCallback((recordingId, detectedDuration) => {
    if (!detectedDuration || detectedDuration <= 0) return;

    updateRecordingDuration(recordingId, detectedDuration).catch((err) => {
      console.warn("Failed to persist detected recording duration:", err);
    });

    setGroupedRecordings((prev) => {
      let changed = false;
      const newGrouped = {};

      for (const sampleType of Object.keys(prev)) {
        newGrouped[sampleType] = prev[sampleType].map((rec) => {
          if (rec.recording_id !== recordingId) return rec;

          const currentDuration = Number(rec.duration) || 0;
          if (!shouldRefreshDuration(currentDuration, detectedDuration)) return rec;

          changed = true;
          const isPassage = isPassageSample(rec);
          const isLongPassage = isPassage && detectedDuration > SEG_LIMIT;

          let annotation = { ...rec.annotation };
          if (isLongPassage) {
            let splitTimes = rec.annotation.split_times || [];
            if (!Array.isArray(splitTimes) || splitTimes.length === 0) {
              splitTimes = generateDefaultSplitTimes(detectedDuration);
            }
            const segments = syncSegmentData(
              splitTimes,
              detectedDuration,
              rec.annotation.segments || []
            );
            annotation = {
              ...annotation,
              split_times: splitTimes,
              segments,
            };
          }

          return {
            ...rec,
            duration: detectedDuration,
            annotation,
          };
        });
      }

      return changed ? newGrouped : prev;
    });
  }, []);

  const handleAnnotationChange = useCallback(
    (recordingId, field, value) => {
      if (isExamSubmitted) return;

      setValidationHighlightIds((prev) => {
        if (!prev[recordingId]) return prev;
        const next = { ...prev };
        delete next[recordingId];
        return next;
      });

      setGroupedRecordings((prev) => {
        const newGrouped = { ...prev };
        const updatedRecordings = newGrouped[activeType].map((rec) => {
          if (rec.recording_id === recordingId) {
            if (field === "transcription_lines") {
              const lines = Array.isArray(value) ? value : [];
              const concatenatedText = joinLinesIntoText(lines);
              return {
                ...rec,
                annotation: {
                  ...rec.annotation,
                  transcription_lines: lines,
                  transcription_text: concatenatedText,
                },
              };
            }
            if (field === "split_times") {
              const duration = Number(rec.duration) || 0;
              const syncedSegments = applySplitTimesChange(
                value,
                duration,
                rec.annotation.split_times || [],
                rec.annotation.segments || []
              );
              const transcriptionText = syncedSegments
                .map((s) => s.transcription_text || "")
                .join("\n---\n");
              return {
                ...rec,
                annotation: {
                  ...rec.annotation,
                  split_times: value,
                  segments: syncedSegments,
                  transcription_text: transcriptionText,
                  transcription_lines: syncedSegments.map((s) => s.transcription_text || ""),
                },
              };
            }
            if (field === "segments") {
              const transcriptionText = (value || [])
                .map((s) => s.transcription_text || "")
                .join("\n---\n");
              return {
                ...rec,
                annotation: {
                  ...rec.annotation,
                  segments: value,
                  transcription_text: transcriptionText,
                  transcription_lines: (value || []).map((s) => s.transcription_text || ""),
                },
              };
            }
            if (field === "not_transcribable") {
              if (isNonTranscribable(value)) {
                return {
                  ...rec,
                  annotation: buildAnnotationClearedForNonTranscribable(rec, value),
                };
              }
              return { ...rec, annotation: { ...rec.annotation, not_transcribable: value } };
            }
            return { ...rec, annotation: { ...rec.annotation, [field]: value } };
          }
          return rec;
        });
        newGrouped[activeType] = updatedRecordings;
        return newGrouped;
      });
    },
    [activeType, isExamSubmitted]
  );

  const handlePreviousTab = () => {
    const currentIndex = sampleTypes.indexOf(activeType);
    if (currentIndex > 0) setActiveType(sampleTypes[currentIndex - 1]);
  };

  const handleNextTab = () => {
    const currentIndex = sampleTypes.indexOf(activeType);
    if (currentIndex < sampleTypes.length - 1) setActiveType(sampleTypes[currentIndex + 1]);
  };

  // Save a single record
  const handleSaveRecord = async (recordingId) => {
    if (!annotatorId) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.notLoggedIn", "Not logged in"),
        message: t("annotator.annotationPage.snackbar.annotatorNotFound", "Annotator not found"),
      });
      return;
    }

    if (isExamSubmitted) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.examSubmittedTitle", "Exam submitted"),
        message: t(
          "annotator.annotationPage.snackbar.cannotSaveSubmitted",
          "Cannot save — exam is already submitted"
        ),
      });
      return;
    }

    setSavingRecord((prev) => ({ ...prev, [recordingId]: true }));

    try {
      const recording = activeRecordings.find((rec) => rec.recording_id === recordingId);
      if (!recording || !canAnnotate(recording)) {
        throw new Error("Cannot save - recording not found or not assigned to you");
      }

      const validation = validateRecordingBeforeSave(recording);
      if (!validation.ok) {
        setValidationHighlightIds((prev) => ({ ...prev, [recordingId]: true }));
        showSnackbar({
          severity: "error",
          title: t("annotator.annotationPage.snackbar.invalidEntry", "Invalid entry"),
          message: formatValidationMessage(validation, t),
        });
        setSavingRecord((prev) => ({ ...prev, [recordingId]: false }));
        return;
      }

      const annotationData = buildAnnotationSavePayload(recording, annotatorId);

      const result = await saveAnnotation(annotationData);
      if (result.status !== "success") {
        throw new Error(result.message || "Failed to save annotation");
      }

      showSnackbar({
        severity: "success",
        title: t("annotator.annotationPage.snackbar.saved", "Saved"),
        message: t(
          "annotator.annotationPage.snackbar.annotationSaved",
          "Annotation saved successfully"
        ),
        duration: 2500,
      });

      await refreshExamData(examId);
    } catch (error) {
      console.error("Failed to save record:", error);
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.saveFailed", "Save failed"),
        message:
          error.message ||
          t("annotator.annotationPage.snackbar.saveAnnotationFailed", "Failed to save annotation"),
      });
    } finally {
      setSavingRecord((prev) => ({ ...prev, [recordingId]: false }));
    }
  };

  const handleSaveCurrentTab = async () => {
    if (!annotatorId || !activeType) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.cannotSave", "Cannot save"),
        message: t(
          "annotator.annotationPage.snackbar.missingAnnotatorOrTab",
          "Missing annotator ID or active tab"
        ),
      });
      return;
    }

    if (isExamSubmitted) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.examSubmittedTitle", "Exam submitted"),
        message: t(
          "annotator.annotationPage.snackbar.cannotSaveSubmitted",
          "Cannot save — exam is already submitted"
        ),
      });
      return;
    }

    setBulkSaving(true);

    try {
      const recordingsToSave = activeRecordings.filter((rec) => canAnnotate(rec));

      if (recordingsToSave.length === 0) {
        showSnackbar({
          severity: "info",
          title: t("annotator.annotationPage.snackbar.noChanges", "No changes"),
          message: t(
            "annotator.annotationPage.snackbar.noRecordingsTab",
            "No recordings to save in this tab"
          ),
        });
        return;
      }

      const invalidRecordings = recordingsToSave.filter(
        (rec) => !validateRecordingBeforeSave(rec).ok
      );
      if (invalidRecordings.length > 0) {
        const firstInvalid = validateRecordingBeforeSave(invalidRecordings[0]);
        setValidationHighlightIds((prev) => ({
          ...prev,
          ...invalidRecordings.reduce((acc, rec) => ({ ...acc, [rec.recording_id]: true }), {}),
        }));
        showSnackbar({
          severity: "error",
          title: t("annotator.annotationPage.snackbar.invalidEntry", "Invalid entry"),
          message: formatValidationMessage(firstInvalid, t),
        });
        return;
      }

      const annotationsData = recordingsToSave.map((recording) =>
        buildAnnotationSavePayload(recording, annotatorId)
      );

      const result = await saveAnnotationsBulk(annotationsData);

      if (result.status === "error") {
        throw new Error(
          formatBulkSaveFailureMessage(
            result,
            t(
              "annotator.annotationPage.snackbar.saveAllFailed",
              "Failed to save annotations. Please try again."
            )
          )
        );
      }

      if (result.status !== "success" && result.status !== "partial_success") {
        throw new Error(result.message || "Failed to save annotations");
      }

      showSnackbar({
        severity: result.status === "success" ? "success" : "warning",
        title:
          result.status === "success"
            ? t("annotator.annotationPage.snackbar.saved", "Saved")
            : t("annotator.annotationPage.snackbar.partiallySaved", "Partially saved"),
        message:
          result.message ||
          t(
            "annotator.annotationPage.snackbar.recordingsSavedCount",
            "Saved {{count}} recording(s)"
          ).replace("{{count}}", String(result.summary?.successful || 0)),
        duration: 3000,
      });

      await refreshExamData(examId);
    } catch (error) {
      console.error("Failed to save annotations:", error);
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.saveFailed", "Save failed"),
        message:
          error.message ||
          t(
            "annotator.annotationPage.snackbar.saveAllFailed",
            "Failed to save annotations. Please try again."
          ),
      });
    } finally {
      setBulkSaving(false);
    }
  };

  const handleSaveAndNext = async () => {
    if (!annotatorId || !activeType) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.cannotSave", "Cannot save"),
        message: t(
          "annotator.annotationPage.snackbar.missingAnnotatorOrTab",
          "Missing annotator ID or active tab"
        ),
      });
      return;
    }

    if (isExamSubmitted) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.examSubmittedTitle", "Exam submitted"),
        message: t(
          "annotator.annotationPage.snackbar.cannotSaveSubmitted",
          "Cannot save — exam is already submitted"
        ),
      });
      return;
    }

    setBulkSaving(true);

    try {
      const recordingsToSave = activeRecordings.filter((rec) => canAnnotate(rec));

      if (recordingsToSave.length === 0) {
        showSnackbar({
          severity: "info",
          title: t("annotator.annotationPage.snackbar.noChanges", "No changes"),
          message: t(
            "annotator.annotationPage.snackbar.noRecordingsTab",
            "No recordings to save in this tab"
          ),
        });
        handleNextTab();
        return;
      }

      const invalidRecordings = recordingsToSave.filter(
        (rec) => !validateRecordingBeforeSave(rec).ok
      );
      if (invalidRecordings.length > 0) {
        const firstInvalid = validateRecordingBeforeSave(invalidRecordings[0]);
        setValidationHighlightIds((prev) => ({
          ...prev,
          ...invalidRecordings.reduce((acc, rec) => ({ ...acc, [rec.recording_id]: true }), {}),
        }));
        showSnackbar({
          severity: "error",
          title: t("annotator.annotationPage.snackbar.invalidEntry", "Invalid entry"),
          message: formatValidationMessage(firstInvalid, t),
        });
        return;
      }

      const annotationsData = recordingsToSave.map((recording) =>
        buildAnnotationSavePayload(recording, annotatorId)
      );

      const result = await saveAnnotationsBulk(annotationsData);

      if (result.status === "error") {
        throw new Error(
          formatBulkSaveFailureMessage(
            result,
            t(
              "annotator.annotationPage.snackbar.saveAllFailed",
              "Failed to save annotations. Please try again."
            )
          )
        );
      }

      if (result.status !== "success" && result.status !== "partial_success") {
        throw new Error(result.message || "Failed to save annotations");
      }

      showSnackbar({
        severity: result.status === "success" ? "success" : "warning",
        title:
          result.status === "success"
            ? t("annotator.annotationPage.snackbar.saved", "Saved")
            : t("annotator.annotationPage.snackbar.partiallySaved", "Partially saved"),
        message:
          result.message ||
          t(
            "annotator.annotationPage.snackbar.recordingsSavedCount",
            "Saved {{count}} recording(s)"
          ).replace("{{count}}", String(result.summary?.successful || 0)),
        duration: 3000,
      });

      const refreshed = await refreshExamData(examId);

      if (refreshed && result.summary?.successful > 0) {
        handleNextTab();
      }
    } catch (error) {
      console.error("Failed to save annotations in bulk:", error);
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.saveFailed", "Save failed"),
        message:
          error.message ||
          t(
            "annotator.annotationPage.snackbar.saveAllFailed",
            "Failed to save annotations. Please try again."
          ),
      });
    } finally {
      setBulkSaving(false);
    }
  };

  // Copy All button function
  const handleCopyAllInCurrentTab = () => {
    if (isExamSubmitted) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.examSubmittedTitle", "Exam submitted"),
        message: t(
          "annotator.annotationPage.snackbar.cannotCopySubmitted",
          "Cannot copy — exam is already submitted"
        ),
      });
      return;
    }

    if (!activeRecordings.length) {
      showSnackbar({
        severity: "info",
        title: t("annotator.annotationPage.snackbar.noRecordings", "No recordings"),
        message: t(
          "annotator.annotationPage.snackbar.noRecordingsCopy",
          "No recordings to copy in this tab"
        ),
      });
      return;
    }

    const editableRecordings = activeRecordings.filter((rec) => canAnnotate(rec));

    if (editableRecordings.length === 0) {
      showSnackbar({
        severity: "warning",
        title: t("annotator.annotationPage.snackbar.noPermission", "No permission"),
        message: t(
          "annotator.annotationPage.snackbar.noEditPermission",
          "You do not have permission to edit any recordings in this tab"
        ),
      });
      return;
    }

    setGroupedRecordings((prev) => {
      const newGrouped = { ...prev };
      const updatedRecordings = newGrouped[activeType].map((rec) => {
        if (!canAnnotate(rec)) {
          return rec;
        }

        const isPassage = isPassageSample(rec);
        const canonicalLines = splitTextIntoLines(rec.sample_text, isPassage);

        const newTranscriptionLines = canonicalLines.map((line) => line);

        return {
          ...rec,
          annotation: {
            ...rec.annotation,
            transcription_lines: newTranscriptionLines,
            transcription_text: joinLinesIntoText(newTranscriptionLines),
          },
        };
      });

      newGrouped[activeType] = updatedRecordings;
      return newGrouped;
    });

    showSnackbar({
      severity: "success",
      title: t("annotator.annotationPage.snackbar.copiedAll", "Copied all"),
      message: t(
        "annotator.annotationPage.snackbar.copiedAllMessage",
        "Copied canonical text for {{count}} recording(s)"
      ).replace("{{count}}", String(editableRecordings.length)),
      duration: 2000,
    });
  };

  const handleOpenCompleteConfirm = () => setConfirmCompleteOpen(true);
  const handleCloseCompleteConfirm = () => {
    if (!markingComplete) setConfirmCompleteOpen(false);
  };

  const handleConfirmComplete = async () => {
    if (!annotatorId || !examId) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.missingData", "Missing data"),
        message: t(
          "annotator.annotationPage.snackbar.missingAnnotatorExam",
          "Missing annotator or exam id."
        ),
      });
      return;
    }

    if (isExamSubmitted) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.alreadySubmitted", "Already submitted"),
        message: t(
          "annotator.annotationPage.snackbar.examAlreadySubmitted",
          "Exam is already submitted"
        ),
      });
      return;
    }

    const allRecordings = Object.values(groupedRecordings).flat();
    const editableRecordings = allRecordings.filter((rec) => canAnnotate(rec));
    const invalidRecordings = editableRecordings.filter(
      (rec) => !validateRecordingBeforeSave(rec, { strict: true }).ok
    );
    if (invalidRecordings.length > 0) {
      const firstInvalid = validateRecordingBeforeSave(invalidRecordings[0], { strict: true });
      setValidationHighlightIds((prev) => ({
        ...prev,
        ...invalidRecordings.reduce((acc, rec) => ({ ...acc, [rec.recording_id]: true }), {}),
      }));
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.invalidEntry", "Invalid entry"),
        message: formatValidationMessage(firstInvalid, t),
      });
      setConfirmCompleteOpen(false);
      return;
    }

    try {
      setMarkingComplete(true);
      const resp = await markExamComplete({ annotator_id: annotatorId, exam_id: examId });
      if (resp?.status === "success") {
        showSnackbar({
          severity: "success",
          title: t("annotator.annotationPage.snackbar.completed", "Completed"),
          message: t(
            "annotator.annotationPage.snackbar.examMarkedComplete",
            "Exam marked complete."
          ),
        });

        const completedExamId = examId;
        let nextAdjacent = null;
        try {
          nextAdjacent = await fetchAdjacentExamsForExam(completedExamId);
        } catch (adjacentErr) {
          console.error("Failed to fetch next in-progress exam:", adjacentErr);
        }

        const nextExamId = nextAdjacent?.next_exam?.exam_id;
        if (nextExamId) {
          handleNavigateToExam(nextExamId);
          showSnackbar({
            severity: "info",
            title: t("annotator.annotationPage.snackbar.loadingNextExam", "Loading next exam"),
            message: t(
              "annotator.annotationPage.snackbar.loadingNextExamMessage",
              "Loading next exam…"
            ),
            duration: 2000,
          });
        } else {
          navigate("/annotator/exams", {
            state: {
              preservedTab: 1,
              showSuccessMessage: true,
              completedExamId,
              timestamp: Date.now(),
            },
          });
        }
      } else {
        throw new Error(resp?.message || "Failed to mark complete");
      }
    } catch (e) {
      console.error(e);
      showSnackbar({
        severity: "error",
        title: t("annotator.annotationPage.snackbar.failed", "Failed"),
        message:
          e.message ||
          t("annotator.annotationPage.snackbar.markCompleteFailed", "Failed to mark complete"),
      });
    } finally {
      setMarkingComplete(false);
      setConfirmCompleteOpen(false);
    }
  };

  const allSubmitted = useMemo(() => {
    if (loading || !Object.keys(groupedRecordings).length) return false;

    for (const sampleType in groupedRecordings) {
      const recordings = groupedRecordings[sampleType];
      const assignedRecordings = recordings.filter((rec) => {
        const isA1 = String(rec.annotator1_id ?? "") === String(annotatorId);
        const isA2 = String(rec.annotator2_id ?? "") === String(annotatorId);
        return isA1 || isA2;
      });

      if (assignedRecordings.length > 0) {
        const allInTypeSubmitted = assignedRecordings.every((rec) => {
          const isA1 = String(rec.annotator1_id ?? "") === String(annotatorId);
          const myStatus = isA1 ? rec.annotation1_status : rec.annotation2_status;
          return (myStatus || "").toLowerCase() === "submitted";
        });
        if (!allInTypeSubmitted) return false;
      }
    }

    return true;
  }, [groupedRecordings, loading, annotatorId]);

  const currentIndex = sampleTypes.indexOf(activeType);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < sampleTypes.length - 1;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box p={3} bgcolor="#fafafa" borderRadius={2} border="1px solid #ddd">
              {/* UPDATED: Sample Details with Navigation */}
              <Card sx={{ p: 2, mb: 3, position: "relative" }}>
                <Grid container spacing={2} alignItems="center">
                  {/* Navigation Buttons on Left */}
                  <Grid item xs={1}>
                    <Box display="flex" gap={1} flexDirection="column">
                      <Tooltip
                        title={t(
                          "annotator.annotationPage.nav.prevExam",
                          "Previous exam (Ctrl + ←)"
                        )}
                      >
                        <span>
                          <IconButton
                            onClick={handlePreviousExam}
                            disabled={!adjacentExams.prev_exam?.exam_id || loadingAdjacent}
                            size="medium"
                            sx={{
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                              bgcolor: adjacentExams.prev_exam?.exam_id ? "white" : "#f5f5f5",
                              "&:hover": {
                                bgcolor: adjacentExams.prev_exam?.exam_id ? "#f0f0f0" : "#f5f5f5",
                              },
                              width: "100%",
                              height: 50,
                            }}
                          >
                            {loadingAdjacent ? (
                              <CircularProgress size={20} />
                            ) : (
                              <ArrowBackIcon fontSize="medium" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Grid>

                  {/* Exam Info */}
                  <Grid item xs={10}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box mt={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">
                              {t("annotator.annotationPage.sessionId", "Session ID")}:{" "}
                              {examId || t("annotator.annotationPage.sessionIdNA", "N/A")}
                            </Typography>
                            <Tooltip
                              title={t("annotator.annotationPage.copySessionId", "Copy session ID")}
                            >
                              <IconButton
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(examId);
                                  showSnackbar({
                                    severity: "success",
                                    title: t("annotator.annotationPage.copied", "Copied"),
                                    message: t(
                                      "annotator.annotationPage.sessionIdCopied",
                                      "Session ID copied to clipboard"
                                    ),
                                    duration: 2000,
                                  });
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Right Navigation */}
                  <Grid item xs={1}>
                    <Box display="flex" gap={1} flexDirection="column">
                      <Tooltip
                        title={t("annotator.annotationPage.nav.nextExam", "Next exam (Ctrl + →)")}
                      >
                        <span>
                          <IconButton
                            onClick={handleNextExam}
                            disabled={!adjacentExams.next_exam?.exam_id || loadingAdjacent}
                            size="medium"
                            sx={{
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                              bgcolor: adjacentExams.next_exam?.exam_id ? "white" : "#f5f5f5",
                              "&:hover": {
                                bgcolor: adjacentExams.next_exam?.exam_id ? "#f0f0f0" : "#f5f5f5",
                              },
                              width: "100%",
                              height: 50,
                            }}
                          >
                            {loadingAdjacent ? (
                              <CircularProgress size={20} />
                            ) : (
                              <ArrowForwardIcon fontSize="medium" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Grid>
                </Grid>
              </Card>

              {!loading && isRejectedByQa && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {t(
                      "annotator.annotationPage.qaRejection.title",
                      "This exam was rejected by QA"
                    )}
                  </Typography>
                  {myQaComment && (
                    <Typography variant="body2" sx={{ mb: 1, whiteSpace: "pre-wrap" }}>
                      <strong>{t("annotator.annotationPage.qaRejection.reason", "Reason")}:</strong>{" "}
                      {myQaComment}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {exam?.qa_reviewed_by_name &&
                      `${t("annotator.annotationPage.qaRejection.reviewedBy", "Reviewed by")}: ${
                        exam.qa_reviewed_by_name
                      }`}
                    {exam?.qa_reviewed_on &&
                      ` · ${t(
                        "annotator.annotationPage.qaRejection.reviewedOn",
                        "Reviewed on"
                      )}: ${new Date(exam.qa_reviewed_on).toLocaleString()}`}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t(
                      "annotator.annotationPage.qaRejection.hint",
                      "Please revise your annotations and submit again."
                    )}
                  </Typography>
                </Alert>
              )}

              {loading ? (
                <Box display="flex" justifyContent="center" py={5}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {/* Custom-styled tabs as buttons */}
                  <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                    <Typography variant="h6" mb={1} fontWeight="bold" color="#333">
                      {t("annotator.annotationPage.verifySamples", "Verify samples")} :
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
                      {sampleTypes.map((label, i) => {
                        const status = getSectionStatus(label);
                        const isActive = label === activeType;
                        return (
                          <Button
                            key={i}
                            onClick={() => setActiveType(label)}
                            variant="outlined"
                            sx={{
                              borderRadius: 1,
                              boxShadow: isActive
                                ? "0 4px 0 0 blue"
                                : status === "completed"
                                ? "0 4px 0 0 green"
                                : "0 4px 0 0 gray",
                              border: "1px solid #ccc",
                              minWidth: 100,
                              color: "#000",
                              textTransform: "none",
                              fontSize: "15px",
                            }}
                          >
                            {translateSampleType(label)}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Annotation Grid */}
                  <Box
                    bgcolor="#fff"
                    px={2}
                    py={0}
                    sx={{
                      boxShadow: 4,
                      borderRadius: 2,
                      overflowX: "auto",
                      border: "1px solid #e0e0e0",
                      mt: 2,
                    }}
                  >
                    {!hasAnyPassage && (
                      <HeaderRow
                        showDisfluency={hasDisfluency}
                        showMistakes={hasNoMistakes}
                        showFluidity={hasFluidity}
                        showTimestamps={hasTimestamps}
                      />
                    )}

                    {activeRecordings.map((row, i) => (
                      <AnnotationRow
                        key={row.recording_id}
                        row={row}
                        index={i}
                        annotatorId={annotatorId}
                        onAnnotationChange={handleAnnotationChange}
                        onDurationDetected={handleDurationDetected}
                        canAnnotate={canAnnotate}
                        savingRecord={savingRecord}
                        onSaveRecord={handleSaveRecord}
                        isExamSubmitted={isExamSubmitted}
                        onViewRemark={handleViewRemark}
                        showDisfluency={hasDisfluency}
                        showMistakes={hasNoMistakes}
                        showFluidity={hasFluidity}
                        showTimestamps={hasTimestamps}
                        showValidationErrors={Boolean(validationHighlightIds[row.recording_id])}
                      />
                    ))}
                    {/* Footer Actions */}
                    <Box py={3} display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" gap={2}>
                        <Button
                          variant="contained"
                          startIcon={<NavigateBeforeIcon />}
                          onClick={handlePreviousTab}
                          disabled={!hasPrevious}
                          sx={{
                            fontWeight: "700",
                            padding: "10px 26px",
                            textTransform: "none",
                            fontSize: "0.95rem",
                            borderRadius: "10px",
                            background: (theme) =>
                              hasPrevious
                                ? "linear-gradient(180deg,#20c997 0%,#17a589 100%)"
                                : "#f0f0f0",
                            color: hasPrevious ? "#fff" : "#9aa0a6",
                            boxShadow: (theme) =>
                              hasPrevious ? "0 6px 14px rgba(32,201,151,0.18)" : "none",
                            transition: "all 180ms ease",
                            "&:hover": {
                              background: hasPrevious
                                ? "linear-gradient(180deg,#17a589 0%,#139077 100%)"
                                : "#f0f0f0",
                              boxShadow: hasPrevious ? "0 8px 20px rgba(23,160,137,0.18)" : "none",
                            },
                            "&:active": { transform: hasPrevious ? "translateY(1px)" : "none" },
                            "&.Mui-disabled": { opacity: 0.6, pointerEvents: "none" },
                          }}
                        >
                          {t("annotator.annotationPage.buttons.previous", "Previous")}
                        </Button>

                        {/* Copy All button */}
                        <Button
                          variant="outlined"
                          startIcon={<ContentCopyIcon />}
                          onClick={handleCopyAllInCurrentTab}
                          disabled={
                            isExamSubmitted || !activeRecordings.some((rec) => canAnnotate(rec))
                          }
                          sx={{
                            fontWeight: "700",
                            padding: "10px 26px",
                            textTransform: "none",
                            fontSize: "0.95rem",
                            borderRadius: "10px",
                            borderColor: "#1d57dd",
                            color: isExamSubmitted ? "#9aa0a6" : "#1d57dd",
                            "&:hover": {
                              borderColor: "#1648b8",
                              backgroundColor: "rgba(29, 87, 221, 0.04)",
                            },
                            "&.Mui-disabled": {
                              opacity: 0.6,
                              pointerEvents: "none",
                            },
                          }}
                        >
                          {t("annotator.annotationPage.buttons.copyAll", "Copy all")}
                        </Button>
                      </Box>

                      <Box display="flex" gap={2}>
                        {!isMyExamCompleted && (
                          <Button
                            variant="contained"
                            onClick={handleOpenCompleteConfirm}
                            disabled={
                              !allSubmitted || markingComplete || bulkSaving || isSavingAnyRecord
                            }
                            sx={{
                              fontWeight: "700",
                              padding: "10px 26px",
                              textTransform: "none",
                              fontSize: "0.95rem",
                              borderRadius: "10px",
                              background: (theme) =>
                                !allSubmitted || markingComplete || bulkSaving || isSavingAnyRecord
                                  ? "#f0f0f0"
                                  : "linear-gradient(180deg,#6f42c1 0%,#5a31a8 100%)",
                              color: (theme) =>
                                !allSubmitted || markingComplete || bulkSaving || isSavingAnyRecord
                                  ? "#9aa0a6"
                                  : "#fff",
                              boxShadow: (theme) =>
                                !allSubmitted || markingComplete || bulkSaving || isSavingAnyRecord
                                  ? "none"
                                  : "0 6px 18px rgba(111,66,193,0.18)",
                              transition: "all 180ms ease",
                              "&:hover": {
                                background:
                                  !allSubmitted ||
                                  markingComplete ||
                                  bulkSaving ||
                                  isSavingAnyRecord
                                    ? "#f0f0f0"
                                    : "linear-gradient(180deg,#5a31a8 0%,#4a248f 100%)",
                              },
                              "&.Mui-disabled": {
                                opacity: 0.6,
                                pointerEvents: "none",
                              },
                            }}
                          >
                            {t(
                              "annotator.annotationPage.buttons.markExamComplete",
                              "Mark exam complete"
                            )}
                          </Button>
                        )}

                        {/* Conditional button: Save & Next for non-last tabs, Save for last tab */}
                        {hasNext ? (
                          <Button
                            variant="contained"
                            endIcon={
                              bulkSaving ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <NavigateNextIcon />
                              )
                            }
                            onClick={handleSaveAndNext}
                            disabled={bulkSaving || isExamSubmitted}
                            sx={{
                              fontWeight: "700",
                              padding: "10px 26px",
                              textTransform: "none",
                              fontSize: "0.95rem",
                              borderRadius: "10px",
                              background: (theme) =>
                                bulkSaving || isExamSubmitted
                                  ? "#f0f0f0"
                                  : "linear-gradient(180deg,#1d57dd 0%,#1648b8 100%)",
                              color: (theme) =>
                                bulkSaving || isExamSubmitted ? "#9aa0a6" : "#fff",
                              boxShadow: (theme) =>
                                bulkSaving || isExamSubmitted
                                  ? "none"
                                  : "0 6px 18px rgba(29,87,221,0.18)",
                              transition: "all 180ms ease",
                              "&:hover": {
                                background:
                                  bulkSaving || isExamSubmitted
                                    ? "#f0f0f0"
                                    : "linear-gradient(180deg,#1648b8 0%,#123a94 100%)",
                              },
                              "&:active": {
                                transform:
                                  !bulkSaving && !isExamSubmitted ? "translateY(1px)" : "none",
                              },
                              "&.Mui-disabled": { opacity: 0.6, pointerEvents: "none" },
                            }}
                          >
                            {bulkSaving
                              ? t("annotator.annotationPage.buttons.saving", "Saving…")
                              : isExamSubmitted
                              ? t("annotator.annotationPage.buttons.submitted", "Submitted")
                              : t("annotator.annotationPage.buttons.saveAndNext", "Save & next")}
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            startIcon={
                              bulkSaving ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <SaveIcon />
                              )
                            }
                            onClick={handleSaveCurrentTab}
                            disabled={bulkSaving || isExamSubmitted}
                            sx={{
                              fontWeight: "700",
                              padding: "10px 26px",
                              textTransform: "none",
                              fontSize: "0.95rem",
                              borderRadius: "10px",
                              background: (theme) =>
                                bulkSaving || isExamSubmitted
                                  ? "#f0f0f0"
                                  : "linear-gradient(180deg,#20c997 0%,#17a589 100%)",
                              color: (theme) =>
                                bulkSaving || isExamSubmitted ? "#9aa0a6" : "#fff",
                              boxShadow: (theme) =>
                                bulkSaving || isExamSubmitted
                                  ? "none"
                                  : "0 6px 18px rgba(32,201,151,0.18)",
                              transition: "all 180ms ease",
                              "&:hover": {
                                background:
                                  bulkSaving || isExamSubmitted
                                    ? "#f0f0f0"
                                    : "linear-gradient(180deg,#17a589 0%,#139077 100%)",
                              },
                              "&:active": {
                                transform:
                                  !bulkSaving && !isExamSubmitted ? "translateY(1px)" : "none",
                              },
                              "&.Mui-disabled": { opacity: 0.6, pointerEvents: "none" },
                            }}
                          >
                            {bulkSaving
                              ? t("annotator.annotationPage.buttons.saving", "Saving…")
                              : isExamSubmitted
                              ? t("annotator.annotationPage.buttons.submitted", "Submitted")
                              : t("annotator.annotationPage.buttons.save", "Save")}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Confirmation Dialog for Mark Exam Complete */}
      <Dialog open={confirmCompleteOpen} onClose={handleCloseCompleteConfirm}>
        <DialogTitle>
          {t("annotator.annotationPage.dialogs.markCompleteTitle", "Confirm exam completion")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              "annotator.annotationPage.dialogs.markCompleteBody",
              "Are you sure you want to mark this exam as complete? This action cannot be undone."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCompleteConfirm} disabled={markingComplete}>
            {t("annotator.annotationPage.buttons.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleConfirmComplete}
            color="error"
            variant="contained"
            disabled={markingComplete}
          >
            {markingComplete ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("annotator.annotationPage.buttons.confirmComplete", "Confirm complete")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Remark Dialog */}
      <Dialog
        open={remarkDialogOpen}
        onClose={() => setRemarkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <CommentIcon color="info" />
              <Typography variant="h6">
                {t("annotator.annotationPage.dialogs.remarkTitle", "Remark from admin")}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setRemarkDialogOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6, fontSize: "0.85rem" }}
          >
            {remarkDialogContent}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemarkDialogOpen(false)} variant="contained" color="info">
            {t("annotator.annotationPage.buttons.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={4000}
        onClose={closeSnackbar}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
          action={
            <IconButton size="small" aria-label="close" color="inherit" onClick={closeSnackbar}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            width: "100%",
            maxWidth: 420,
            borderRadius: 2,
            boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
            px: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: 700 }}>
              {snackbar.title ||
                (snackbar.severity === "error"
                  ? t("annotator.annotationPage.snackbar.error", "Error")
                  : t("annotator.annotationPage.snackbar.success", "Success"))}
            </Typography>
          </Box>
          <Box sx={{ px: 2, py: 1, background: "transparent" }}>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.95)" }}>
              {snackbar.message}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
